// src/pages/employer/JobAdMockup.jsx
// Editable mockup for a Job Ad — mirrors the public-facing job listing layout.
// Stage 2: hover highlights, rich text editing for description/howToApply,
//          plain text editing for position descriptions, title modal, closing date,
//          share/print/QR icons, sequential ad number.

import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { jobAdAPI, orgAPI, snippetAPI } from '../../services/api';
import { useConfirm, EditBtn, DelBtn, AddBtn, DocH1, usePersistedState } from '../../components/shared/UI';
import { SectionModal, LANGUAGES } from '../../components/shared/FieldModals';
import toast from 'react-hot-toast';
import {
  ArrowLeft, ChevronRight, CheckCircle, X,
  Share2, Printer, QrCode, RotateCcw, RotateCw, ChevronDown,
  Bold, Italic, List, ListOrdered,
  Mail, Phone, ExternalLink, Clipboard,
} from 'lucide-react';

// ── Wireframe palette ─────────────────────────────────────
const C = {
  blue:         '#3D80CC',
  navy:         '#0e4e95',
  teal:         '#0D4E69',
  maroon:       '#990000',
  adTitle:      '#0066CC',
  posRowBorder: '#0066CC',
  posRowBg:     '#E6E6E6',
  sidebarBg:    '#f5f5f5',
  closingBg:    '#D9EDF7',
  white:        '#ffffff',
  docBorder:    '#B3B3B3',
};

const STATUS = {
  DRAFT:    { label: 'Draft',    color: '#777',    bg: '#f5f5f5' },
  ACTIVE:   { label: 'Active',   color: '#28A745', bg: '#e8f5e9' },
  PAUSED:   { label: 'Paused',   color: '#E67E22', bg: '#FEF9E7' },
  CLOSED:   { label: 'Closed',   color: '#CC0000', bg: '#fde8e8' },
  ARCHIVED: { label: 'Archived', color: '#555',    bg: '#eee' },
};

const newId = () => crypto.randomUUID();

const inputStyle = {
  width: '100%', padding: '7px 10px', borderRadius: 6,
  border: '1px solid var(--border)', fontSize: 13,
  background: 'white', color: 'var(--text)', outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit',
};
const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--secondary)',
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4,
};
const fieldWrap = { marginBottom: 14 };

const REQUIREMENT_OPTIONS = [
  { value: '',          label: 'Unspecified' },
  { value: 'REQUIRED',  label: 'Required'    },
  { value: 'PREFERRED', label: 'Preferred'   },
];

const RadioGroup = ({ name = 'req', value, onChange }) => (
  <div style={{ display: 'flex', gap: 18, marginTop: 4 }}>
    {REQUIREMENT_OPTIONS.map(opt => (
      <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 13 }}>
        <input type="radio" name={name} value={opt.value} checked={value === opt.value}
          onChange={() => onChange(opt.value)}
          style={{ accentColor: 'var(--secondary)', width: 14, height: 14, cursor: 'pointer' }} />
        {opt.label}
      </label>
    ))}
  </div>
);

const SCHEDULE_LABEL = {
  FULL_TIME: 'Full Time', PART_TIME: 'Part Time', CASUAL: 'Casual', CONTRACT: 'Contract',
};

const ADMIN_LEVEL_LABEL = {
  UNSPECIFIED: 'Unspecified', VOLUNTEER: 'Volunteer', OFFICER: 'Officer',
  SUPERVISOR: 'Supervisor', ASSISTANT_MANAGER: 'Assistant Manager',
  MANAGER: 'Manager', SENIOR_MANAGER: 'Senior Manager', EXECUTIVE_MANAGER: 'Executive Manager',
};

const ENVIRONMENT_LABEL = {
  OFFICE: 'Office', TRANSPORTATION: 'Transportation', EDUCATIONAL: 'Educational',
  RETAIL: 'Retail', HOSPITALITY: 'Hospitality / Hotel', MANUFACTURING: 'Manufacturing',
  UNIVERSITY: 'University / School', GOVERNMENT: 'Government', CONSTRUCTION: 'Construction',
  OUTDOORS: 'Outdoors', HEALTHCARE: 'Healthcare', FARMING: 'Farming / Agriculture', OTHER: 'Other',
};

const SCHEDULES_LIST = [
  { value: '',           label: '— Not specified —' },
  { value: 'FULL_TIME',  label: 'Full Time' },
  { value: 'PART_TIME',  label: 'Part Time' },
  { value: 'CASUAL',     label: 'Casual' },
  { value: 'CONTRACT',   label: 'Contract' },
];

const SALARY_PERIODS = [
  { value: '',       label: '— Period —'  },
  { value: 'HOURLY', label: 'Per Hour'   },
  { value: 'DAILY',  label: 'Per Day'    },
  { value: 'WEEKLY', label: 'Per Week'   },
  { value: 'ANNUAL', label: 'Per Year'   },
];

const SALARY_PERIOD_LABEL = { HOURLY: '/hr', DAILY: '/day', WEEKLY: '/wk', ANNUAL: '/yr' };

const EXP_YEAR_LABEL = {
  '1': '1+ years', '2': '2+ years', '3': '3+ years',
  '5': '5+ years', '7': '7+ years', '10': '10+ years',
};

const IDEAL_SKILL_SUBCATEGORIES = [
  { key: 'general',  label: 'General Skills'                   },
  { key: 'soft',     label: 'Soft / People Skills'             },
  { key: 'software', label: 'Software & Application Knowledge' },
  { key: 'hardware', label: 'Hardware & Equipment Knowledge'   },
];

const EDUCATION_LEVELS = [
  { value: '',                        label: '— Not required —'        },
  { value: 'High School Certificate', label: 'High School Certificate' },
  { value: 'Diploma',                 label: 'Diploma'                 },
  { value: 'Advanced Diploma',        label: 'Advanced Diploma'        },
  { value: 'Graduate Certificate',    label: 'Graduate Certificate'    },
  { value: 'Graduate Diploma',        label: 'Graduate Diploma'        },
  { value: 'Bachelor Degree',         label: 'Bachelor Degree'         },
  { value: 'Master Degree',           label: 'Master Degree'           },
  { value: 'Doctorate',               label: 'Doctorate'               },
];

const EXP_MIN_YEARS = [
  { value: '',   label: 'Any experience' },
  { value: '1',  label: '1+ years'       },
  { value: '2',  label: '2+ years'       },
  { value: '3',  label: '3+ years'       },
  { value: '5',  label: '5+ years'       },
  { value: '7',  label: '7+ years'       },
  { value: '10', label: '10+ years'      },
];

const BENEFIT_CATEGORIES = [
  { key: 'health',      label: 'Health & Wellbeing'  },
  { key: 'financial',   label: 'Financial'            },
  { key: 'flexibility', label: 'Flexibility & Leave'  },
  { key: 'growth',      label: 'Learning & Growth'    },
  { key: 'perks',       label: 'Perks & Lifestyle'    },
];

const CURRENCIES = ['AUD', 'USD', 'GBP', 'EUR', 'SGD', 'NZD'];

// ── Editable hover wrapper ────────────────────────────────
const Editable = ({ onClick, children, style = {} }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative', cursor: 'pointer', borderRadius: 4,
        outline: hover ? `2px dashed ${C.blue}` : '2px dashed transparent',
        transition: 'outline 0.1s',
        ...style,
      }}
    >
      {hover && (
        <span style={{
          position: 'absolute', top: -11, right: 0, zIndex: 10,
          background: C.blue, color: '#fff', fontSize: 9, fontWeight: 700,
          padding: '1px 6px', borderRadius: 2, pointerEvents: 'none',
          letterSpacing: '0.06em',
        }}>EDIT</span>
      )}
      {children}
    </div>
  );
};

// ── Snippet dropdown (shared by both modals) ──────────────
const SnippetDropdown = ({ snippets, onSelect, onClose }) => {
  if (!snippets.length) return null;
  return (
    <div style={{
      position: 'absolute', top: '100%', left: 0, zIndex: 50,
      background: 'white', border: '1px solid #ddd', borderRadius: 4,
      boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 200, maxHeight: 220, overflowY: 'auto',
    }}>
      {snippets.map(s => (
        <div
          key={s.id}
          onMouseDown={e => { e.preventDefault(); onSelect(s); onClose(); }}
          style={{ padding: '7px 12px', cursor: 'pointer', fontSize: 13, color: '#333' }}
          onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >
          {s.name}
        </div>
      ))}
    </div>
  );
};

// ── Rich text modal (bold, italic, UL, OL) ────────────────
const RichTextModal = ({ title, initialHtml, maxChars, snippets, onSave, onCancel }) => {
  const editorRef             = useRef(null);
  const savedRangeRef         = useRef(null);
  const [charCount, setCharCount] = useState(0);
  const [saving, setSaving]       = useState(false);
  const [snippetOpen, setSnippetOpen] = useState(false);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.innerHTML = initialHtml || '';
    setCharCount((editor.innerText || '').length);
    editor.focus();
  }, []); // eslint-disable-line

  useEffect(() => {
    const editor = editorRef.current;

    const onSelChange = () => {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const r = sel.getRangeAt(0);
        if (editor && editor.contains(r.commonAncestorContainer)) {
          savedRangeRef.current = r.cloneRange();
        }
      }
    };

    const onDocMousedown = (e) => {
      const btn = e.target.closest('[data-rte-cmd]');
      if (!btn) return;
      e.preventDefault();

      const ed = editorRef.current;
      if (ed) {
        ed.focus();
        if (savedRangeRef.current) {
          try {
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(savedRangeRef.current);
          } catch (_) {}
        }
      }

      const cmd = btn.getAttribute('data-rte-cmd');
      if (cmd === 'bold')   document.execCommand('bold',                false, null);
      if (cmd === 'italic') document.execCommand('italic',              false, null);
      if (cmd === 'ul')     document.execCommand('insertUnorderedList', false, null);
      if (cmd === 'ol')     document.execCommand('insertOrderedList',   false, null);

      const ed2 = editorRef.current;
      if (ed2) setCharCount((ed2.innerText || '').length);
    };

    document.addEventListener('selectionchange', onSelChange);
    document.addEventListener('mousedown', onDocMousedown);
    return () => {
      document.removeEventListener('selectionchange', onSelChange);
      document.removeEventListener('mousedown', onDocMousedown);
    };
  }, []); // eslint-disable-line

  const insertSnippet = (snippet) => {
    const ed = editorRef.current;
    if (!ed) return;
    ed.focus();
    if (savedRangeRef.current) {
      try {
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(savedRangeRef.current);
      } catch (_) {}
    }
    document.execCommand('insertHTML', false, snippet.content);
    setCharCount((ed.innerText || '').length);
  };

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(editorRef.current?.innerHTML || ''); } finally { setSaving(false); }
  };

  const tbStyle = {
    background: 'none', border: '1px solid #ddd', borderRadius: 3,
    padding: '4px 7px', cursor: 'pointer', display: 'flex', alignItems: 'center',
    color: '#333', lineHeight: 1,
  };
  const sep = <div style={{ width: 1, height: 20, background: '#ddd', margin: '0 2px' }} />;

  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={() => setSnippetOpen(false)}>
      <div className="modal-box" style={{ maxWidth: 640, width: '94vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{title}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} style={{ padding: '4px 6px' }}>
            <X size={15} />
          </button>
        </div>
        <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 5, alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: 10 }}>
            <button type="button" title="Bold"          data-rte-cmd="bold"   style={tbStyle}><Bold size={14} /></button>
            <button type="button" title="Italic"        data-rte-cmd="italic" style={tbStyle}><Italic size={14} /></button>
            {sep}
            <button type="button" title="Bullet list"   data-rte-cmd="ul"     style={tbStyle}><List size={14} /></button>
            <button type="button" title="Numbered list" data-rte-cmd="ol"     style={tbStyle}><ListOrdered size={14} /></button>
            {snippets.length > 0 && (
              <>
                {sep}
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    title="Insert snippet"
                    onMouseDown={e => { e.preventDefault(); setSnippetOpen(v => !v); }}
                    style={{ ...tbStyle, gap: 4 }}
                  >
                    <Clipboard size={13} />
                    <span style={{ fontSize: 12 }}>Snippets</span>
                    <ChevronDown size={11} />
                  </button>
                  {snippetOpen && (
                    <SnippetDropdown
                      snippets={snippets}
                      onSelect={insertSnippet}
                      onClose={() => setSnippetOpen(false)}
                    />
                  )}
                </div>
              </>
            )}
          </div>
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            className="rte-editor"
            onInput={() => setCharCount((editorRef.current?.innerText || '').length)}
            style={{
              flex: 1, minHeight: 220, border: '1px solid var(--border)', borderRadius: 4,
              padding: '10px 12px', fontSize: 14, lineHeight: 1.65,
              outline: 'none', background: 'white', overflowY: 'auto',
              fontFamily: "'DM Sans', sans-serif",
            }}
          />
          <div style={{ fontSize: 11, textAlign: 'right', color: charCount > maxChars ? '#CC0000' : '#999' }}>
            {charCount} / {maxChars}
          </div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || charCount > maxChars}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Plain text modal (no formatting) ─────────────────────
const PlainTextModal = ({ title, initialText, maxChars, snippets, onSave, onCancel }) => {
  const [text, setText]           = useState(initialText || '');
  const [saving, setSaving]       = useState(false);
  const [snippetOpen, setSnippetOpen] = useState(false);
  const textareaRef               = useRef(null);
  const cursorPosRef              = useRef(null);

  const stripHtml = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.innerText || div.textContent || '';
  };

  const insertSnippet = (snippet) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const pos = cursorPosRef.current ?? ta.selectionStart ?? text.length;
    const plain = stripHtml(snippet.content);
    const newText = text.slice(0, pos) + plain + text.slice(pos);
    setText(newText);
    // Restore cursor after inserted text
    setTimeout(() => {
      ta.selectionStart = ta.selectionEnd = pos + plain.length;
      ta.focus();
    }, 0);
  };

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(text); } finally { setSaving(false); }
  };

  const tbStyle = {
    background: 'none', border: '1px solid #ddd', borderRadius: 3,
    padding: '4px 7px', cursor: 'pointer', display: 'flex', alignItems: 'center',
    color: '#333', lineHeight: 1, fontSize: 12,
  };
  const sep = <div style={{ width: 1, height: 20, background: '#ddd', margin: '0 2px' }} />;

  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={() => setSnippetOpen(false)}>
      <div className="modal-box" style={{ maxWidth: 580, width: '94vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{title}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} style={{ padding: '4px 6px' }}>
            <X size={15} />
          </button>
        </div>
        <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {snippets.length > 0 && (
            <div style={{ display: 'flex', gap: 5, alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: 10 }}>
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  title="Insert snippet"
                  onClick={() => setSnippetOpen(v => !v)}
                  style={{ ...tbStyle, gap: 4 }}
                >
                  <Clipboard size={13} />
                  <span>Snippets</span>
                  <ChevronDown size={11} />
                </button>
                {snippetOpen && (
                  <SnippetDropdown
                    snippets={snippets}
                    onSelect={insertSnippet}
                    onClose={() => setSnippetOpen(false)}
                  />
                )}
              </div>
              {sep}
              <span style={{ fontSize: 11, color: '#999' }}>Snippets are inserted as plain text</span>
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onSelect={e => { cursorPosRef.current = e.target.selectionStart; }}
            onClick={e => { cursorPosRef.current = e.target.selectionStart; }}
            autoFocus
            style={{ ...inputStyle, minHeight: 200, resize: 'vertical' }}
          />
          <div style={{ fontSize: 11, textAlign: 'right', color: text.length > maxChars ? '#CC0000' : '#999' }}>
            {text.length} / {maxChars}
          </div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave}
            disabled={saving || text.length > maxChars}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Title edit modal ──────────────────────────────────────
const TitleModal = ({ currentTitle, positions, onSave, onCancel }) => {
  const defaultTitle = positions.map((p) => p.jobDefinition?.name || 'Position').join(' · ');
  const [title, setTitle] = useState(currentTitle || defaultTitle);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try { await onSave(title.trim()); } finally { setSaving(false); }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div className="modal-box" style={{ maxWidth: 500, width: '92vw' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Edit Job Ad Title</h3>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} style={{ padding: '4px 6px' }}>
            <X size={15} />
          </button>
        </div>
        <div className="modal-body" style={{ padding: '20px 24px' }}>
          <div style={fieldWrap}>
            <label style={labelStyle}>Title *</label>
            <input
              type="text" value={title} maxLength={128}
              onChange={(e) => setTitle(e.target.value)}
              style={inputStyle} autoFocus
            />
            <div style={{ fontSize: 11, textAlign: 'right', marginTop: 3, color: title.length >= 120 ? '#CC0000' : '#999' }}>
              {title.length} / 128
            </div>
          </div>
          {defaultTitle && (
            <button
              onClick={() => setTitle(defaultTitle)}
              style={{
                background: 'none', border: 'none', color: C.blue,
                fontSize: 12, cursor: 'pointer', padding: 0,
                display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'inherit',
              }}
            >
              <RotateCcw size={11} /> Reset to position names
            </button>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave}
            disabled={saving || !title.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── QR code coming-soon modal ─────────────────────────────
const QrModal = ({ onClose }) => ReactDOM.createPortal(
  <div className="modal-overlay" style={{ zIndex: 1100 }}>
    <div className="modal-box" style={{ maxWidth: 340, width: '90vw' }}>
      <div className="modal-header">
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>QR Code</h3>
        <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: '4px 6px' }}>
          <X size={15} />
        </button>
      </div>
      <div className="modal-body" style={{ padding: '32px 24px', textAlign: 'center' }}>
        <QrCode size={52} color={C.blue} style={{ margin: '0 auto 14px', display: 'block' }} />
        <p style={{ color: '#555', fontSize: 14, margin: 0 }}>
          QR code generation is coming soon.
        </p>
      </div>
    </div>
  </div>,
  document.body
);

// ── Add Position modal ────────────────────────────────────
const AddPositionModal = ({ jobDefs, allAds, currentAdId, onAdd, onCancel }) => {
  const [selected, setSelected] = useState(null);
  const [adding, setAdding]     = useState(false);

  const findLastPos = (jobDefId) => {
    const candidates = [];
    for (const a of (allAds || [])) {
      if (a.id === currentAdId) continue;
      if (!['ACTIVE', 'CLOSED', 'ARCHIVED'].includes(a.status)) continue;
      for (const p of (a.positions || [])) {
        if (p.jobDefinitionId === jobDefId) candidates.push(p);
      }
    }
    return candidates.length ? candidates[candidates.length - 1] : null;
  };

  const handleAdd = async () => {
    if (!selected) return;
    setAdding(true);
    try {
      const jd   = jobDefs.find((d) => d.id === selected);
      const last = findLastPos(selected);
      await onAdd({
        jobDefinitionId:        selected,
        workplaceId:            jd?.workplaceId  || null,
        schedule:               jd?.schedule     || null,
        salaryMin:              last?.salaryMin  ?? null,
        salaryMax:              last?.salaryMax  ?? null,
        salaryCurrency:         last?.salaryCurrency || 'AUD',
        salaryPeriod:           last?.salaryPeriod   || null,
        salaryVisible:          last?.salaryVisible  !== undefined ? last.salaryVisible : true,
        benefits:               last?.benefits   || null,
        openToForeignNationals: true,
        vacancies: 1,
      });
    } finally { setAdding(false); }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div className="modal-box" style={{ maxWidth: 540, width: '92vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Add Position</h3>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} style={{ padding: '4px 6px' }}><X size={15} /></button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto', flex: 1, padding: '16px 24px' }}>
          {jobDefs.length === 0 ? (
            <p style={{ color: '#999', fontStyle: 'italic', fontSize: 13 }}>
              No positions defined in this profile yet. Add positions under Profile → Positions first.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {jobDefs.map((jd) => {
                const last       = findLastPos(jd.id);
                const isSelected = selected === jd.id;
                return (
                  <div
                    key={jd.id}
                    onClick={() => setSelected(jd.id)}
                    style={{
                      border: `2px solid ${isSelected ? C.blue : 'var(--border)'}`,
                      borderRadius: 6, padding: '10px 14px', cursor: 'pointer',
                      background: isSelected ? '#EBF3FC' : 'white',
                      transition: 'all 0.12s',
                    }}
                  >
                    <div style={{ fontSize: 14, fontWeight: 700, color: isSelected ? C.navy : 'var(--text)' }}>{jd.name}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 4, alignItems: 'center' }}>
                      {jd.seniorityLevel && jd.seniorityLevel !== 'UNSPECIFIED' && (
                        <span style={{ fontSize: 11, fontWeight: 600, background: '#EDE9FE', color: '#6D28D9', border: '1px solid #C4B5FD', borderRadius: 10, padding: '1px 7px' }}>
                          {ADMIN_LEVEL_LABEL[jd.seniorityLevel]}
                        </span>
                      )}
                      {jd.schedule && (
                        <span style={{ fontSize: 11, fontWeight: 600, background: '#E0F2FE', color: '#0369A1', border: '1px solid #BAE6FD', borderRadius: 10, padding: '1px 7px' }}>
                          {SCHEDULE_LABEL[jd.schedule]}
                        </span>
                      )}
                      {jd.workplace && (
                        <span style={{ fontSize: 11, color: '#666' }}>📍 {jd.workplace.name}</span>
                      )}
                    </div>
                    {last && (last.salaryMin != null || last.salaryMax != null) && (
                      <div style={{ fontSize: 11, color: '#888', marginTop: 3, fontStyle: 'italic' }}>
                        Last salary: {last.salaryCurrency || 'AUD'} {[last.salaryMin, last.salaryMax].filter(Boolean).map((v) => v.toLocaleString()).join(' – ')} {last.salaryPeriod ? SALARY_PERIOD_LABEL[last.salaryPeriod] : ''} — will be pre-filled
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={!selected || adding}>
            {adding ? 'Adding…' : 'Add Position'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Edit Position Circumstances modal ─────────────────────
const EditPositionCircumstancesModal = ({ position, onSave, onCancel }) => {
  const [form, setForm] = useState({
    schedule:               position.schedule               || '',
    salaryMin:              position.salaryMin              != null ? String(position.salaryMin) : '',
    salaryMax:              position.salaryMax              != null ? String(position.salaryMax) : '',
    salaryVisible:          position.salaryVisible          !== false,
    openToForeignNationals: position.openToForeignNationals !== false,
    adminLevel:             position.seniorityLevel || position.jobDefinition?.seniorityLevel || 'UNSPECIFIED',
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        schedule:               form.schedule || null,
        salaryMin:              form.salaryMin !== '' ? parseFloat(form.salaryMin) : null,
        salaryMax:              form.salaryMax !== '' ? parseFloat(form.salaryMax) : null,
        salaryVisible:          form.salaryVisible,
        openToForeignNationals: form.openToForeignNationals,
        adminLevel:             form.adminLevel,
      });
    } finally { setSaving(false); }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div className="modal-box" style={{ maxWidth: 520, width: '94vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Edit Position Circumstances</h3>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} style={{ padding: '4px 6px' }}><X size={15} /></button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto', flex: 1, padding: '16px 24px' }}>
          {[
            { id: 'schedule', label: 'Schedule',
              control: (
                <select value={form.schedule} onChange={(e) => setForm((f) => ({ ...f, schedule: e.target.value }))} style={inputStyle}>
                  {SCHEDULES_LIST.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              )},
            { id: 'adminLevel', label: 'Admin Level',
              control: (
                <select value={form.adminLevel} onChange={(e) => setForm((f) => ({ ...f, adminLevel: e.target.value }))} style={inputStyle}>
                  {Object.entries(ADMIN_LEVEL_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              )},
            { id: 'salaryMin', label: 'Minimum Salary',
              control: (
                <input type="number" min="0" value={form.salaryMin}
                  onChange={(e) => setForm((f) => ({ ...f, salaryMin: e.target.value }))}
                  style={inputStyle} placeholder="e.g. 80000" />
              )},
            { id: 'salaryMax', label: 'Maximum Salary',
              control: (
                <input type="number" min="0" value={form.salaryMax}
                  onChange={(e) => setForm((f) => ({ ...f, salaryMax: e.target.value }))}
                  style={inputStyle} placeholder="e.g. 100000" />
              )},
          ].map(({ id, label, control }) => (
            <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <label htmlFor={id} style={{ ...labelStyle, display: 'block', width: 150, flexShrink: 0, marginBottom: 0 }}>{label}</label>
              <div style={{ flex: 1 }}>{control}</div>
            </div>
          ))}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <span style={{ ...labelStyle, width: 150, flexShrink: 0, marginBottom: 0 }}>Show Salary</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" id="salVis" checked={form.salaryVisible}
                onChange={(e) => setForm((f) => ({ ...f, salaryVisible: e.target.checked }))} style={{ width: 14, height: 14 }} />
              Show salary to candidates
            </label>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <span style={{ ...labelStyle, width: 150, flexShrink: 0, marginBottom: 0 }}>Foreign Nationals</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" id="openFN" checked={form.openToForeignNationals}
                onChange={(e) => setForm((f) => ({ ...f, openToForeignNationals: e.target.checked }))} style={{ width: 14, height: 14 }} />
              Open to foreign nationals
            </label>
          </div>

        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Edit Position Benefits modal ───────────────────────────
const EditPositionBenefitsModal = ({ position, profileBenefits, onSave, onCancel }) => {
  const [benefits, setBenefits] = useState(
    Array.isArray(position.benefits) ? [...position.benefits] : []
  );
  const [saving, setSaving] = useState(false);

  const toggle = (b) => setBenefits((prev) =>
    prev.includes(b) ? prev.filter((x) => x !== b) : [...prev, b]
  );

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(benefits.length ? benefits : null); } finally { setSaving(false); }
  };

  const hasAnyBenefit = profileBenefits &&
    BENEFIT_CATEGORIES.some(({ key }) => Array.isArray(profileBenefits[key]) && profileBenefits[key].length > 0);

  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div className="modal-box" style={{ maxWidth: 540, width: '92vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Position Benefits</h3>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} style={{ padding: '4px 6px' }}><X size={15} /></button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto', flex: 1, padding: '16px 24px' }}>
          {!hasAnyBenefit ? (
            <p style={{ fontSize: 12, color: '#999', fontStyle: 'italic', margin: 0 }}>
              No benefits configured on the profile yet. Add benefits under Profile → Benefits first.
            </p>
          ) : (
            BENEFIT_CATEGORIES.map(({ key, label }) => {
              const items = Array.isArray(profileBenefits?.[key]) ? profileBenefits[key] : [];
              if (!items.length) return null;
              return (
                <div key={key} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{label}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {items.map((b) => {
                      const active = benefits.includes(b);
                      return (
                        <label key={b} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                          <input type="checkbox" checked={active} onChange={() => toggle(b)}
                            style={{ width: 14, height: 14, accentColor: C.blue, cursor: 'pointer' }} />
                          {b}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Section label — H1 heading ────────────────────────────
const SectionLabel = ({ children }) => (
  <DocH1 color={C.teal} style={{ marginTop: 16, marginBottom: 6 }}>{children}</DocH1>
);

// ── Requirement label (Required / Preferred) ──────────────
const ReqLabel = ({ req }) => {
  if (!req) return null;
  return req === 'REQUIRED'
    ? <strong style={{ fontSize: 12 }}> (Required)</strong>
    : <span style={{ fontSize: 12, fontStyle: 'italic', fontWeight: 'normal' }}> (Preferred)</span>;
};

// ── Blue section bar ──────────────────────────────────────
const SectionBar = ({ children }) => (
  <div style={{
    background: C.blue, color: 'white',
    fontSize: 14, fontWeight: 500, padding: '6px 14px',
  }}>{children}</div>
);

// ── Sidebar section header ────────────────────────────────
const SidebarBar = ({ children }) => (
  <div style={{
    background: C.blue, color: 'white',
    fontSize: 11, fontWeight: 600, padding: '5px 10px', letterSpacing: '0.04em',
  }}>{children}</div>
);

// ── Sidebar placeholder block ─────────────────────────────
const SidebarPlaceholder = ({ children, minHeight = 70 }) => (
  <div style={{
    margin: '6px 8px', padding: 8, fontSize: 11,
    color: '#666', fontStyle: 'italic', borderRadius: 2,
    minHeight, lineHeight: 1.6,
  }}>{children}</div>
);

// ── Select workplace modal ────────────────────────────────
const SelectWorkplaceModal = ({ workplaces, currentWorkplaceId, currentHideWorkplaceName, onSave, onCancel }) => {
  const [selected, setSelected]   = useState(currentWorkplaceId || '');
  const [hideName, setHideName]   = useState(!!currentHideWorkplaceName);
  const [saving, setSaving]       = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(selected || null, hideName); } finally { setSaving(false); }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div className="modal-box" style={{ maxWidth: 480, width: '92vw' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Select Workplace</h3>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} style={{ padding: '4px 6px' }}>
            <X size={15} />
          </button>
        </div>
        <div className="modal-body" style={{ padding: '20px 24px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
              borderRadius: 6, border: `1px solid ${selected === '' ? C.blue : 'var(--border)'}`,
              background: selected === '' ? '#EBF3FC' : 'white', cursor: 'pointer', fontSize: 13,
            }}>
              <input type="radio" name="wp" value="" checked={selected === ''}
                onChange={() => setSelected('')} style={{ accentColor: C.blue }} />
              <em style={{ color: '#888' }}>None (use job definition default)</em>
            </label>
            {workplaces.map((w) => {
              const addr = w.address && typeof w.address === 'object' ? w.address : null;
              const loc  = [addr?.suburb, addr?.state].filter(Boolean).join(', ');
              return (
                <label key={w.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px',
                  borderRadius: 6, border: `1px solid ${selected === w.id ? C.blue : 'var(--border)'}`,
                  background: selected === w.id ? '#EBF3FC' : 'white', cursor: 'pointer',
                }}>
                  <input type="radio" name="wp" value={w.id} checked={selected === w.id}
                    onChange={() => setSelected(w.id)} style={{ accentColor: C.blue, marginTop: 2 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{w.name}</div>
                    {loc && <div style={{ fontSize: 11, color: '#777', marginTop: 1 }}>{loc}</div>}
                    {w.environment && (
                      <div style={{ fontSize: 11, color: '#777' }}>
                        {ENVIRONMENT_LABEL[w.environment] || w.environment}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
            {workplaces.length === 0 && (
              <p style={{ fontSize: 13, color: '#888', fontStyle: 'italic', margin: 0 }}>
                No workplaces defined for this profile yet.
              </p>
            )}
            <label style={{
              display: 'flex', alignItems: 'center', gap: 8, marginTop: 4,
              fontSize: 13, color: '#444', cursor: 'pointer', userSelect: 'none',
            }}>
              <input type="checkbox" checked={hideName} onChange={(e) => setHideName(e.target.checked)}
                style={{ accentColor: C.blue, width: 14, height: 14 }} />
              Hide workplace name
            </label>
          </div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Sync confirmation modal ───────────────────────────────
const SYNC_SECTION_LABEL = {
  description:    'Position Description',
  role:           'Role & Main Tasks',
  benefits:       'Benefits',
  'ic.languages': 'Languages (Expected Profile)',
  'ic.education': 'Education (Expected Profile)',
  'ic.experience':'Experience (Expected Profile)',
  'ic.skills':    'Skills & Knowledge (Expected Profile)',
  details:        'Details (Admin Level & Schedule)',
  workplace:      'Workplace',
};

// ── Sync preview sub-components ──────────────────────────
const SyncDescriptionPreview = ({ text }) => {
  if (!text) return <em style={{ color: '#aaa', fontSize: 12 }}>No description</em>;
  return <p style={{ margin: 0, fontSize: 12, color: '#333', lineHeight: 1.5 }}>{text}</p>;
};

const SyncRolePreview = ({ role }) => {
  if (!role) return <em style={{ color: '#aaa', fontSize: 12 }}>Role not found in template</em>;
  return (
    <div style={{ fontSize: 12, color: '#333' }}>
      <strong>{role.title || '(untitled)'}</strong>
      {Array.isArray(role.tasks) && role.tasks.length > 0 && (
        <ol style={{ margin: '4px 0 0', paddingLeft: 16 }}>
          {role.tasks.map((t, i) => <li key={t.id || i} style={{ marginBottom: 2 }}>{t.text}</li>)}
        </ol>
      )}
      {(!role.tasks || role.tasks.length === 0) && (
        <span style={{ color: '#aaa', fontStyle: 'italic' }}> — no tasks</span>
      )}
    </div>
  );
};

const SyncBenefitsPreview = ({ benefits }) => {
  const list = Array.isArray(benefits) ? benefits : [];
  if (!list.length) return <em style={{ color: '#aaa', fontSize: 12 }}>No benefits defined</em>;
  return (
    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#333' }}>
      {list.map((b, i) => <li key={i} style={{ marginBottom: 2 }}>{b}</li>)}
    </ul>
  );
};

const SyncIcLanguagesPreview = ({ ic }) => {
  const langs = Array.isArray((ic || {}).languages) ? ic.languages : [];
  if (!langs.length) return <em style={{ color: '#aaa', fontSize: 12 }}>None defined</em>;
  return (
    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#333' }}>
      {langs.map((l, i) => {
        const name = typeof l === 'string' ? l : l.name;
        const req  = typeof l === 'string' ? null : l.requirement;
        return <li key={i} style={{ marginBottom: 2 }}>{name}{req ? ` (${req === 'REQUIRED' ? 'Required' : 'Preferred'})` : ''}</li>;
      })}
    </ul>
  );
};

const SyncIcEducationPreview = ({ ic }) => {
  const edu = Array.isArray((ic || {}).education) ? ic.education : [];
  if (!edu.length) return <em style={{ color: '#aaa', fontSize: 12 }}>None defined</em>;
  return (
    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#333' }}>
      {edu.map((e, i) => (
        <li key={i} style={{ marginBottom: 2 }}>
          {e.minLevel ? `${e.minLevel} in ` : ''}{e.fieldOfStudy}
          {e.requirement ? ` (${e.requirement === 'REQUIRED' ? 'Required' : 'Preferred'})` : ''}
        </li>
      ))}
    </ul>
  );
};

const SyncIcExperiencePreview = ({ ic }) => {
  const exp = Array.isArray((ic || {}).experience) ? ic.experience : [];
  if (!exp.length) return <em style={{ color: '#aaa', fontSize: 12 }}>None defined</em>;
  return (
    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#333' }}>
      {exp.map((e, i) => (
        <li key={i} style={{ marginBottom: 2 }}>
          {e.minYears ? (EXP_YEAR_LABEL[e.minYears] || `${e.minYears}+ years`) : 'Any experience'}
          {e.positionName ? ` as ${e.positionName}` : ''}
          {e.requirement ? ` (${e.requirement === 'REQUIRED' ? 'Required' : 'Preferred'})` : ''}
        </li>
      ))}
    </ul>
  );
};

const SyncIcSkillsCatPreview = ({ ic, catKey, catLabel }) => {
  const skills = ((ic || {}).skills && !Array.isArray((ic || {}).skills)) ? (ic || {}).skills : {};
  const items = Array.isArray(skills[catKey]) ? skills[catKey] : [];
  if (!items.length) return <em style={{ color: '#aaa', fontSize: 12 }}>None in {catLabel}</em>;
  return (
    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#333' }}>
      {items.map((s, i) => (
        <li key={s.id || i} style={{ marginBottom: 2 }}>
          {s.name}{s.requirement ? ` (${s.requirement === 'REQUIRED' ? 'Required' : 'Preferred'})` : ''}
        </li>
      ))}
    </ul>
  );
};

// Legacy previews (kept for details / workplace sections)
const SyncDetailsPreview = ({ pos, isAd }) => {
  const sl = isAd ? pos.seniorityLevel : pos.jobDefinition?.seniorityLevel;
  const sc = isAd ? pos.schedule       : pos.jobDefinition?.schedule;
  if (!sl && !sc) return <em style={{ color: '#aaa', fontSize: 12 }}>Not set</em>;
  return (
    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#333' }}>
      {sl && sl !== 'UNSPECIFIED' && <li><strong>Admin Level:</strong> {ADMIN_LEVEL_LABEL[sl] || sl}</li>}
      {sc && <li><strong>Schedule:</strong> {SCHEDULE_LABEL[sc] || sc}</li>}
    </ul>
  );
};

const SyncWorkplacePreview = ({ pos, isAd }) => {
  const wp = isAd ? pos.workplace : pos.jobDefinition?.workplace;
  if (!wp) return <em style={{ color: '#aaa', fontSize: 12 }}>Not set</em>;
  return <span style={{ fontSize: 12, color: '#333' }}>{wp.name}</span>;
};

const SyncModal = ({ pos, section, roleId, catKey, catLabel, onConfirm, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const jd = pos.jobDefinition;
  const colStyle = { flex: 1, minWidth: 0, padding: '10px 14px', background: '#fafafa', borderRadius: 6, border: '1px solid #ddd' };
  const colHeadStyle = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, color: '#555' };

  const renderPreview = (isAd) => {
    const ic = isAd ? pos.idealCandidate : jd?.idealCandidate;
    if (section === 'description')   return <SyncDescriptionPreview text={isAd ? pos.description : jd?.description} />;
    if (section === 'role') {
      const roles = isAd ? pos.roles : jd?.roles;
      const role = Array.isArray(roles) ? roles.find(r => r.id === roleId) : null;
      return <SyncRolePreview role={role} />;
    }
    if (section === 'benefits')      return <SyncBenefitsPreview benefits={isAd ? pos.benefits : jd?.benefits} />;
    if (section === 'ic.languages')  return <SyncIcLanguagesPreview ic={ic} />;
    if (section === 'ic.education')  return <SyncIcEducationPreview ic={ic} />;
    if (section === 'ic.experience') return <SyncIcExperiencePreview ic={ic} />;
    if (section === 'ic.skills')     return <SyncIcSkillsCatPreview ic={ic} catKey={catKey} catLabel={catLabel} />;
    if (section === 'details')       return <SyncDetailsPreview pos={pos} isAd={isAd} />;
    if (section === 'workplace')     return <SyncWorkplacePreview pos={pos} isAd={isAd} />;
    return null;
  };

  const modalTitle = (() => {
    if (section === 'role') {
      const role = Array.isArray(pos.roles) ? pos.roles.find(r => r.id === roleId) : null;
      return `Sync: ${role?.title || 'Role'}`;
    }
    if (section === 'ic.skills') return `Sync: ${catLabel || 'Skills'}`;
    return `Sync: ${SYNC_SECTION_LABEL[section] || section}`;
  })();

  const handleConfirm = async () => {
    setSaving(true);
    try { await onConfirm(); } finally { setSaving(false); }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onCancel()}>
      <div className="modal-box" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <span>{modalTitle}</span>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} style={{ padding: 4 }}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 12, color: '#666', marginBottom: 14 }}>
            The <strong>ad position</strong> content will overwrite the <strong>profile template</strong>. This cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={colStyle}>
              <div style={{ ...colHeadStyle, color: C.blue }}>This Ad Position</div>
              {renderPreview(true)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', fontSize: 18, color: '#aaa', padding: '0 4px' }}>→</div>
            <div style={colStyle}>
              <div style={{ ...colHeadStyle, color: '#777' }}>Profile Template (will be overwritten)</div>
              {renderPreview(false)}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleConfirm} disabled={saving}
            style={{ background: C.teal, borderColor: C.teal }}>
            {saving ? 'Syncing…' : 'Confirm Sync ↑'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Select Contacts modal ─────────────────────────────────
const SelectContactsModal = ({ contacts, selectedIds, workplaceContactIds, onSave, onCancel }) => {
  const [checked, setChecked] = useState(new Set(selectedIds));
  const [saving, setSaving]   = useState(false);

  const toggle = (id) => setChecked(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleSave = async () => {
    setSaving(true);
    try { await onSave([...checked]); } finally { setSaving(false); }
  };

  // Workplace-associated contacts first, then the rest
  const wpSet   = new Set(workplaceContactIds);
  const wpContacts    = contacts.filter(c => wpSet.has(c.id));
  const otherContacts = contacts.filter(c => !wpSet.has(c.id));

  const renderContact = (c) => (
    <label key={c.id} style={{
      display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px',
      borderRadius: 6, cursor: 'pointer', userSelect: 'none',
      border: `1px solid ${checked.has(c.id) ? C.blue : 'var(--border)'}`,
      background: checked.has(c.id) ? '#EBF3FC' : 'white',
    }}>
      <input type="checkbox" checked={checked.has(c.id)} onChange={() => toggle(c.id)}
        style={{ accentColor: C.blue, marginTop: 2, width: 14, height: 14, flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#333' }}>{c.name}</div>
        {c.emails?.[0] && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: C.blue, marginTop: 2 }}>
            <Mail size={11} style={{ flexShrink: 0 }} />
            {c.emails[0].email || c.emails[0].value}
          </div>
        )}
        {c.phones?.[0] && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#555', marginTop: 1 }}>
            <Phone size={11} style={{ flexShrink: 0 }} />
            {c.phones[0].phone || [c.phones[0].code, c.phones[0].number].filter(Boolean).join(' ')}
          </div>
        )}
      </div>
    </label>
  );

  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div className="modal-box" style={{ maxWidth: 500, width: '92vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Select Contacts</h3>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} style={{ padding: '4px 6px' }}>
            <X size={15} />
          </button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto', flex: 1, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {wpContacts.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.teal, textTransform: 'uppercase',
                letterSpacing: '0.06em', marginBottom: 4 }}>Workplace Contacts</div>
              {wpContacts.map(renderContact)}
              {otherContacts.length > 0 && (
                <div style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase',
                  letterSpacing: '0.06em', margin: '8px 0 4px' }}>Other Contacts</div>
              )}
            </>
          )}
          {otherContacts.map(renderContact)}
          {contacts.length === 0 && (
            <p style={{ fontSize: 13, color: '#888', fontStyle: 'italic', margin: 0 }}>
              No contacts defined for this profile yet.
            </p>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── ICP: Languages modal ──────────────────────────────────
const IcpLanguagesModal = ({ initial, onSave, onCancel }) => {
  const [items, setItems] = useState(() =>
    (initial || []).map(item => typeof item === 'string' ? { id: newId(), name: item, requirement: '' } : { ...item })
  );
  const [input, setInput]       = useState('');
  const [saving, setSaving]     = useState(false);
  const [showList, setShowList] = useState(false);
  const [dropPos, setDropPos]   = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const wrapRef  = useRef(null);

  const selectedNames = items.map(i => i.name);
  const filtered = LANGUAGES.filter(l => l.toLowerCase().includes(input.toLowerCase()) && !selectedNames.includes(l)).slice(0, 8);

  const openDrop = () => {
    if (wrapRef.current) {
      const rect = wrapRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
    }
    setShowList(true);
  };

  const addLang = (name) => {
    setItems(prev => [...prev, { id: newId(), name, requirement: '' }]);
    setInput(''); setShowList(false); inputRef.current?.focus();
  };

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(items); } finally { setSaving(false); }
  };

  return (
    <SectionModal title="Languages" saving={saving} onSave={handleSave} onCancel={onCancel}>
      <div style={fieldWrap} ref={wrapRef}>
        <label style={labelStyle}>Add Language</label>
        <input ref={inputRef} type="text" value={input}
          onChange={e => { setInput(e.target.value); openDrop(); }}
          onFocus={openDrop} onBlur={() => setTimeout(() => setShowList(false), 150)}
          style={inputStyle} placeholder="Type to search languages…" autoFocus />
        {showList && filtered.length > 0 && ReactDOM.createPortal(
          <div style={{ position: 'absolute', top: dropPos.top, left: dropPos.left, width: dropPos.width,
            zIndex: 9999, background: 'white', border: '1px solid var(--border)', borderRadius: 6,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' }}>
            {filtered.map(l => (
              <div key={l} onMouseDown={() => addLang(l)}
                style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', color: 'var(--text)' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#F0F7FF'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}>{l}</div>
            ))}
          </div>, document.body
        )}
      </div>
      {items.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map(item => (
            <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
              background: '#F8FBFF', border: '1px solid #C8D8EC', borderRadius: 6, padding: '6px 10px' }}>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{item.name}</span>
              <RadioGroup name={`lang-${item.id}`} value={item.requirement || ''}
                onChange={req => setItems(prev => prev.map(i => i.id === item.id ? { ...i, requirement: req } : i))} />
              <DelBtn onClick={() => setItems(prev => prev.filter(i => i.id !== item.id))} />
            </div>
          ))}
        </div>
      ) : (
        <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', margin: 0 }}>No languages added yet.</p>
      )}
    </SectionModal>
  );
};

// ── ICP: Education modal ──────────────────────────────────
const IcpEducationModal = ({ initial, onSave, onCancel }) => {
  const [items, setItems]         = useState(() => (initial || []).map(e => ({ ...e })));
  const [fieldOfStudy, setFos]    = useState('');
  const [minLevel, setMinLevel]   = useState('');
  const [requirement, setReq]     = useState('');
  const [saving, setSaving]       = useState(false);

  const addItem = () => {
    if (!fieldOfStudy.trim()) return;
    setItems(prev => [...prev, { id: newId(), fieldOfStudy: fieldOfStudy.trim(), minLevel, requirement }]);
    setFos(''); setMinLevel(''); setReq('');
  };

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(items); } finally { setSaving(false); }
  };

  return (
    <SectionModal title="Education Requirements" saving={saving} onSave={handleSave} onCancel={onCancel}>
      {/* Add form */}
      <div style={{ background: '#F8FBFF', border: '1px solid #C8D8EC', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
        <div style={fieldWrap}>
          <label style={labelStyle}>Field of Study <span style={{ color: '#CC0000' }}>*</span></label>
          <input value={fieldOfStudy} onChange={e => setFos(e.target.value)}
            style={inputStyle} placeholder="e.g. Accounting, Computer Science…" autoFocus />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Qualification Level</label>
          <select value={minLevel} onChange={e => setMinLevel(e.target.value)} style={inputStyle}>
            {EDUCATION_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Requirement</label>
          <RadioGroup name="edu-req-new" value={requirement} onChange={setReq} />
        </div>
        <button className="btn btn-secondary btn-sm" onClick={addItem} disabled={!fieldOfStudy.trim()}>
          + Add
        </button>
      </div>
      {/* Current items */}
      {items.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map(e => (
            <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
              background: 'white', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px' }}>
              <span style={{ flex: 1, fontSize: 13 }}>
                {e.minLevel ? `${e.minLevel} in ` : ''}{e.fieldOfStudy}
                {e.requirement === 'REQUIRED' && <strong style={{ fontSize: 12 }}> (Required)</strong>}
                {e.requirement === 'PREFERRED' && <em style={{ fontSize: 12 }}> (Preferred)</em>}
              </span>
              <DelBtn onClick={() => setItems(prev => prev.filter(i => i.id !== e.id))} />
            </div>
          ))}
        </div>
      ) : (
        <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', margin: 0 }}>No education requirements added yet.</p>
      )}
    </SectionModal>
  );
};

// ── ICP: Experience modal ─────────────────────────────────
const IcpExperienceModal = ({ initial, onSave, onCancel }) => {
  const [items, setItems]           = useState(() => (initial || []).map(e => ({ ...e })));
  const [positionName, setPosName]  = useState('');
  const [minYears, setMinYears]     = useState('');
  const [requirement, setReq]       = useState('');
  const [saving, setSaving]         = useState(false);

  const addItem = () => {
    setItems(prev => [...prev, { id: newId(), positionName: positionName.trim(), minYears, requirement }]);
    setPosName(''); setMinYears(''); setReq('');
  };

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(items); } finally { setSaving(false); }
  };

  return (
    <SectionModal title="Experience Requirements" saving={saving} onSave={handleSave} onCancel={onCancel}>
      {/* Add form */}
      <div style={{ background: '#F8FBFF', border: '1px solid #C8D8EC', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
        <div style={fieldWrap}>
          <label style={labelStyle}>Position Name</label>
          <input value={positionName} onChange={e => setPosName(e.target.value)}
            style={inputStyle} placeholder="e.g. Project Manager, Senior Accountant…" autoFocus />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Duration</label>
          <select value={minYears} onChange={e => setMinYears(e.target.value)} style={inputStyle}>
            {EXP_MIN_YEARS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={labelStyle}>Requirement</label>
          <RadioGroup name="exp-req-new" value={requirement} onChange={setReq} />
        </div>
        <button className="btn btn-secondary btn-sm" onClick={addItem}>+ Add</button>
      </div>
      {/* Current items */}
      {items.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map(e => (
            <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
              background: 'white', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px' }}>
              <span style={{ flex: 1, fontSize: 13 }}>
                {e.minYears ? (EXP_YEAR_LABEL[e.minYears] || `${e.minYears}+ years`) : 'Any experience'}
                {e.positionName ? ` as ${e.positionName}` : ''}
                {e.requirement === 'REQUIRED' && <strong style={{ fontSize: 12 }}> (Required)</strong>}
                {e.requirement === 'PREFERRED' && <em style={{ fontSize: 12 }}> (Preferred)</em>}
              </span>
              <DelBtn onClick={() => setItems(prev => prev.filter(i => i.id !== e.id))} />
            </div>
          ))}
        </div>
      ) : (
        <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', margin: 0 }}>No experience requirements added yet.</p>
      )}
    </SectionModal>
  );
};

// ── ICP: Skills modal (single category) ──────────────────
const IcpSkillsModal = ({ title, initial, onSave, onCancel }) => {
  const [items, setItems] = useState(() => (initial || []).map(s => ({ ...s })));
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const addSkill = () => {
    const name = input.trim();
    if (!name) return;
    setItems(prev => [...prev, { id: newId(), name, requirement: '' }]);
    setInput(''); inputRef.current?.focus();
  };

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(items); } finally { setSaving(false); }
  };

  return (
    <SectionModal title={title || 'Skills & Knowledge'} saving={saving} onSave={handleSave} onCancel={onCancel}>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Add Skill</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
            style={{ ...inputStyle, flex: 1 }} placeholder="Type a skill and press Enter…" autoFocus />
          <AddBtn onClick={addSkill} title="Add" />
        </div>
      </div>
      {items.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map(s => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
              background: '#F8FBFF', border: '1px solid #C8D8EC', borderRadius: 6, padding: '6px 10px' }}>
              <span style={{ flex: 1, fontSize: 13 }}>{s.name}</span>
              <RadioGroup name={`skill-${s.id}`} value={s.requirement || ''}
                onChange={req => setItems(prev => prev.map(i => i.id === s.id ? { ...i, requirement: req } : i))} />
              <DelBtn onClick={() => setItems(prev => prev.filter(i => i.id !== s.id))} />
            </div>
          ))}
        </div>
      ) : (
        <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', margin: 0 }}>No skills added yet.</p>
      )}
    </SectionModal>
  );
};

// ── ICP: Add new skill category modal ────────────────────
const IcpAddSkillCategoryModal = ({ onSave, onCancel }) => {
  const [categoryName, setCatName] = useState('');
  const [items, setItems]          = useState([]);
  const [input, setInput]          = useState('');
  const [saving, setSaving]        = useState(false);
  const inputRef = useRef(null);

  const addSkill = () => {
    const name = input.trim();
    if (!name) return;
    setItems(prev => [...prev, { id: newId(), name, requirement: '' }]);
    setInput(''); inputRef.current?.focus();
  };

  const handleSave = async () => {
    if (!categoryName.trim()) return;
    setSaving(true);
    try { await onSave({ label: categoryName.trim(), key: newId(), items }); } finally { setSaving(false); }
  };

  return (
    <SectionModal title="Add Skills Section" saving={saving} onSave={handleSave} onCancel={onCancel}
      disabled={!categoryName.trim()}>
      <div style={fieldWrap}>
        <label style={labelStyle}>Section Name <span style={{ color: '#CC0000' }}>*</span></label>
        <input value={categoryName} onChange={e => setCatName(e.target.value)}
          style={inputStyle} placeholder="e.g. Industry Knowledge, Certifications…" autoFocus />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Add Skill</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
            style={{ ...inputStyle, flex: 1 }} placeholder="Type a skill and press Enter…" />
          <AddBtn onClick={addSkill} title="Add" />
        </div>
      </div>
      {items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {items.map(s => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
              background: '#F8FBFF', border: '1px solid #C8D8EC', borderRadius: 6, padding: '6px 10px' }}>
              <span style={{ flex: 1, fontSize: 13 }}>{s.name}</span>
              <RadioGroup name={`newskill-${s.id}`} value={s.requirement || ''}
                onChange={req => setItems(prev => prev.map(i => i.id === s.id ? { ...i, requirement: req } : i))} />
              <DelBtn onClick={() => setItems(prev => prev.filter(i => i.id !== s.id))} />
            </div>
          ))}
        </div>
      )}
    </SectionModal>
  );
};

// ── Edit Role modal ───────────────────────────────────────
const EditRoleModal = ({ role, roleIndex, onSave, onCancel }) => {
  const [title, setTitle]   = useState(role.title || '');
  const [tasks, setTasks]   = useState(() => (role.tasks || []).map((t) => ({ ...t })));
  const [saving, setSaving] = useState(false);
  const inputRefs           = useRef({});
  const pendingFocusId      = useRef(null);

  useEffect(() => {
    if (pendingFocusId.current && inputRefs.current[pendingFocusId.current]) {
      inputRefs.current[pendingFocusId.current].focus();
      pendingFocusId.current = null;
    }
  });

  const addTask = () => {
    if (tasks.length > 0 && !tasks[tasks.length - 1].text.trim()) return;
    const id = newId();
    setTasks((prev) => [...prev, { id, text: '' }]);
    pendingFocusId.current = id;
  };
  const removeTask = (id) => setTasks((prev) => prev.filter((t) => t.id !== id));
  const updateTask = (id, text) => setTasks((prev) => prev.map((t) => t.id === id ? { ...t, text } : t));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(role.id, { title, tasks: tasks.filter((t) => t.text.trim()) });
    } finally { setSaving(false); }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 1100 }}>
      <div className="modal-box" style={{ maxWidth: 560, width: '92vw' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
            Edit Role {roleIndex + 1}
          </h3>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} style={{ padding: '4px 6px' }}>
            <X size={15} />
          </button>
        </div>
        <div className="modal-body" style={{ padding: '20px 24px' }}>
          {/* Role title */}
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Role Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={inputStyle}
              placeholder="e.g. Financial Reporting, Client Management…"
              autoFocus
            />
          </div>

          {/* Tasks */}
          <div>
            <label style={labelStyle}>Main Tasks</label>
            {tasks.map((task, ti) => (
              <div key={task.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0, width: 18, textAlign: 'right' }}>
                  {ti + 1}.
                </span>
                <input
                  ref={(el) => { inputRefs.current[task.id] = el; }}
                  value={task.text}
                  onChange={(e) => updateTask(task.id, e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTask(); } }}
                  style={inputStyle}
                  placeholder="Describe a main task…"
                />
                <DelBtn onClick={() => removeTask(task.id)} />
              </div>
            ))}
            <AddBtn onClick={addTask} title="Add Task" />
          </div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !title.trim()}>
            {saving ? 'Saving…' : 'Save Role'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Collapsible sub-section within a position ─────────────
const PosSubSection = ({ title, posId, subKey, children, titleSuffix }) => {
  const [open, setOpen] = usePersistedState(`tikhet:mockup:sub:${posId}:${subKey}`, true);
  return (
    <div style={{ marginTop: 8 }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '5px 10px', cursor: 'pointer', userSelect: 'none',
          background: '#EEF4FA', borderTop: '1px solid #D0E2F2',
          borderBottom: open ? '1px solid #D0E2F2' : 'none',
        }}
      >
        <ChevronRight size={13} color={C.teal}
          style={{ flexShrink: 0, transition: 'transform 0.2s', transform: open ? 'rotate(90deg)' : 'none' }} />
        <span style={{ fontSize: 12, fontWeight: 700, color: C.teal,
          textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 }}>{title}</span>
        {titleSuffix && <span onClick={e => e.stopPropagation()}>{titleSuffix}</span>}
      </div>
      {open && <div>{children}</div>}
    </div>
  );
};

// ── Mode dropdown ─────────────────────────────────────────
const ModeDropdown = ({ mode, onChange, adStatus }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const modes = [
    { value: 'PREVIEW', label: 'Preview' },
    { value: 'EDIT',    label: 'Edit'    },
    ...(adStatus === 'DRAFT' ? [{ value: 'SYNC', label: 'Sync' }] : []),
  ];
  const modeColors     = { PREVIEW: 'transparent', EDIT: C.blue, SYNC: C.teal };
  const modeTextColors = { PREVIEW: C.blue, EDIT: 'white', SYNC: 'white' };
  const borderColor    = mode === 'SYNC' ? C.teal : C.blue;
  const rect = ref.current?.getBoundingClientRect();
  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 3,
          background: modeColors[mode] || 'transparent',
          border: `1px solid ${borderColor}`,
          color: modeTextColors[mode] || C.blue,
          borderRadius: 10, padding: '1px 8px',
          fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        {mode === 'PREVIEW' ? 'Preview' : mode === 'EDIT' ? 'Editing' : 'Sync'}
        <ChevronDown size={10} />
      </button>
      {open && ReactDOM.createPortal(
        <>
          {/* Transparent backdrop to catch outside clicks */}
          <div style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
            onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          {/* Dropdown menu */}
          <div style={{
            position: 'fixed',
            top: (rect?.bottom || 0) + 4,
            right: window.innerWidth - (rect?.right || 0),
            background: 'white', border: '1px solid #ddd', borderRadius: 6,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 9999, minWidth: 90,
          }}>
            {modes.map(m => (
              <div key={m.value}
                onClick={(e) => { e.stopPropagation(); onChange(m.value); setOpen(false); }}
                style={{
                  padding: '7px 14px', fontSize: 12, cursor: 'pointer',
                  color: m.value === mode ? C.blue : '#333',
                  fontWeight: m.value === mode ? 700 : 400,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                {m.label}
              </div>
            ))}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

// ── Position row ──────────────────────────────────────────
const PositionRow = ({ position, expanded, onToggle, isAdmin, mode, onChangeMode, canUndo, canRedo, onUndo, onRedo, undoRedoLoading, adStatus, onEditDescription, onRemove, onEditCircumstances, onEditBenefits, onEditWorkplace, onEditRole, onAddRole, onEditIdeal, onSync }) => {
  const isEditMode = mode === 'EDIT';
  const isSyncMode = mode === 'SYNC';
  const jd        = position.jobDefinition;
  const name      = jd?.name || 'Position';
  const wp        = position.workplace || jd?.workplace || null;
  const schedule  = position.schedule || jd?.schedule;
  const rawSeniority = position.seniorityLevel || jd?.seniorityLevel;
  const adminLevel = rawSeniority && rawSeniority !== 'UNSPECIFIED'
    ? ADMIN_LEVEL_LABEL[rawSeniority] : null;
  const hasSalary = position.salaryMin != null || position.salaryMax != null;
  const salaryStr = hasSalary ? [
    position.salaryCurrency || 'AUD',
    position.salaryMin  != null ? position.salaryMin.toLocaleString()  : null,
    position.salaryMax  != null ? `– ${position.salaryMax.toLocaleString()}` : null,
    position.salaryPeriod ? SALARY_PERIOD_LABEL[position.salaryPeriod] : null,
  ].filter(Boolean).join(' ') : null;
  const benefits   = Array.isArray(position.benefits) ? position.benefits : [];
  const roles      = Array.isArray(position.roles) ? position.roles : (Array.isArray(jd?.roles) ? jd.roles : []);
  const ic         = (position.idealCandidate && typeof position.idealCandidate === 'object') ? position.idealCandidate
                   : (jd?.idealCandidate && typeof jd.idealCandidate === 'object') ? jd.idealCandidate : {};
  const icLangs    = Array.isArray(ic.languages)  ? ic.languages  : [];
  const icEdu      = Array.isArray(ic.education)  ? ic.education  : [];
  const icExp      = Array.isArray(ic.experience) ? ic.experience : [];
  const icSkillsObj = (ic.skills && !Array.isArray(ic.skills)) ? ic.skills : {};
  const hasIdealData = icLangs.length > 0 || icEdu.length > 0 || icExp.length > 0 ||
    IDEAL_SKILL_SUBCATEGORIES.some(cat => (icSkillsObj[cat.key] || []).length > 0);
  const envLabel   = wp?.environment ? (ENVIRONMENT_LABEL[wp.environment] || wp.environment) : null;
  const languages  = Array.isArray(wp?.workingLanguages) ? wp.workingLanguages : [];
  const addr       = wp?.address && typeof wp.address === 'object' ? wp.address : null;
  const locationStr = [addr?.suburb, addr?.state].filter(Boolean).join(', ');

  const circLabelStyle = { color: C.teal, fontWeight: 600 };
  const tagStyle = {
    display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: 11,
    background: '#f0f0f0', border: '1px solid #ccc', color: '#444',
  };

  // Section label with optional sync button (shown in sync mode)
  const SyncBtn = ({ section, roleId, catKey, catLabel }) => isSyncMode ? (
    <button
      onClick={(e) => { e.stopPropagation(); onSync(section, { roleId, catKey, catLabel }); }}
      style={{
        fontSize: 10, fontWeight: 700, color: C.teal, background: '#E6F4F1',
        border: `1px solid ${C.teal}70`, borderRadius: 10, padding: '1px 9px',
        cursor: 'pointer', fontFamily: 'inherit', marginRight: 4, flexShrink: 0,
      }}
    >Sync ↑</button>
  ) : null;

  const SL = ({ children, section }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 16 }}>
      <DocH1 color={C.teal} style={{ flex: 1, marginBottom: 6 }}>{children}</DocH1>
      <SyncBtn section={section} />
    </div>
  );

  return (
    <div style={{ marginBottom: 4 }}>
      {/* Name row */}
      <div
        onClick={onToggle}
        style={{
          background: isEditMode ? '#E0EDFC' : C.posRowBg,
          border: `0.5px solid ${C.posRowBorder}40`,
          borderLeft: `3px solid ${isEditMode ? C.blue : C.posRowBorder}`,
          padding: '7px 12px',
          display: 'flex', alignItems: 'center',
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
          <span style={{ fontSize: 14, color: C.maroon }}>{name}</span>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginRight: 6 }}
            onClick={e => e.stopPropagation()}>
            {isEditMode && (
              <>
                <button onClick={onUndo} disabled={!canUndo || undoRedoLoading} title="Undo"
                  style={{ background: 'none', border: `1px solid ${canUndo ? C.blue : '#ccc'}`, borderRadius: 6,
                    padding: '2px 5px', cursor: canUndo ? 'pointer' : 'default',
                    color: canUndo ? C.blue : '#ccc', display: 'flex', alignItems: 'center' }}>
                  <RotateCcw size={11} />
                </button>
                <button onClick={onRedo} disabled={!canRedo || undoRedoLoading} title="Redo"
                  style={{ background: 'none', border: `1px solid ${canRedo ? C.blue : '#ccc'}`, borderRadius: 6,
                    padding: '2px 5px', cursor: canRedo ? 'pointer' : 'default',
                    color: canRedo ? C.blue : '#ccc', display: 'flex', alignItems: 'center' }}>
                  <RotateCw size={11} />
                </button>
              </>
            )}
            <ModeDropdown mode={mode} onChange={onChangeMode} adStatus={adStatus} />
          </div>
        )}
        <ChevronRight
          size={16} color={C.adTitle}
          style={{ flexShrink: 0, transition: 'transform 0.2s', transform: expanded ? 'rotate(90deg)' : 'none' }}
        />
      </div>

      {/* Expanded body */}
      {expanded && (
        <div style={{ borderLeft: `3px solid ${isEditMode ? C.blue : C.posRowBorder}20`, paddingBottom: 10 }}>

          {/* Mode info strip */}
          {isAdmin && isEditMode && (
            <div style={{ background: '#EBF3FC', padding: '5px 12px', borderBottom: `1px solid ${C.blue}25`,
              fontSize: 11, color: '#555' }}>
              Changes save to this ad only. Use <strong>Sync</strong> mode to push to the position template.
            </div>
          )}
          {isAdmin && isSyncMode && (
            <div style={{ background: '#E6F4F1', padding: '5px 12px', borderBottom: `1px solid ${C.teal}30`,
              fontSize: 11, color: C.teal, fontWeight: 600 }}>
              Sync mode — click a section's Sync button to push it to the position template.
            </div>
          )}

          {/* ── Sub-section: Position ── */}
          <PosSubSection title="Position" posId={position.id} subKey="pos">

            {/* Details */}
            <SL section="details">Details</SL>
            {(isAdmin && isEditMode) ? (
              <Editable onClick={onEditCircumstances}>
                <div style={{ padding: '4px 6px' }}>
                  {!schedule && !adminLevel && !hasSalary ? (
                    <em style={{ color: '#aaa', fontSize: 12 }}>Click to edit circumstances…</em>
                  ) : (
                    <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                      {schedule   && <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}><span style={circLabelStyle}>Schedule: </span>{SCHEDULE_LABEL[schedule] || schedule}</li>}
                      {adminLevel && <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}><span style={circLabelStyle}>Admin Level: </span>{adminLevel}</li>}
                      {hasSalary  && (
                        <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}>
                          <span style={circLabelStyle}>Salary: </span>{salaryStr}
                          {!position.salaryVisible && <em style={{ color: '#999', marginLeft: 6, fontSize: 11 }}>(not shown to candidates)</em>}
                        </li>
                      )}
                      <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}>
                        <span style={circLabelStyle}>Foreign Nationals: </span>
                        {position.openToForeignNationals !== false ? 'Open' : 'Not open'}
                      </li>
                    </ul>
                  )}
                </div>
              </Editable>
            ) : (
              <div style={{ padding: '4px 6px' }}>
                <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                  {schedule   && <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}><span style={circLabelStyle}>Schedule: </span>{SCHEDULE_LABEL[schedule] || schedule}</li>}
                  {adminLevel && <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}><span style={circLabelStyle}>Admin Level: </span>{adminLevel}</li>}
                  {hasSalary  && <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}><span style={circLabelStyle}>Salary: </span>{salaryStr}</li>}
                  <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}>
                    <span style={circLabelStyle}>Foreign Nationals: </span>
                    {position.openToForeignNationals !== false ? 'Open' : 'Not open'}
                  </li>
                </ul>
              </div>
            )}

            {/* Workplace */}
            <SL section="workplace">Workplace</SL>
            {(isAdmin && isEditMode) ? (
              <Editable onClick={onEditWorkplace}>
                <div style={{ padding: '4px 6px' }}>
                  {wp ? (
                    <>
                      {!position.hideWorkplaceName && (
                        <div className="doc-h2" style={{ color: '#444', marginBottom: 4 }}>{wp.name}</div>
                      )}
                      <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                        {envLabel    && <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}><span style={circLabelStyle}>Environment: </span>{envLabel}</li>}
                        {languages.length > 0 && <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}><span style={circLabelStyle}>Languages: </span>{languages.join(', ')}</li>}
                        {locationStr && <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}><span style={circLabelStyle}>Location: </span>{locationStr}</li>}
                      </ul>
                    </>
                  ) : (
                    <em style={{ color: '#aaa', fontSize: 12 }}>Click to assign a workplace…</em>
                  )}
                </div>
              </Editable>
            ) : (
              <div style={{ padding: '0 6px' }}>
                {wp ? (
                  <>
                    {!position.hideWorkplaceName && (
                      <div className="doc-h2" style={{ color: '#444', marginBottom: 4 }}>{wp.name}</div>
                    )}
                    <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                      {envLabel    && <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}><span style={circLabelStyle}>Environment: </span>{envLabel}</li>}
                      {languages.length > 0 && <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}><span style={circLabelStyle}>Languages: </span>{languages.join(', ')}</li>}
                      {locationStr && <li style={{ fontSize: 12, color: '#444', marginBottom: 2 }}><span style={circLabelStyle}>Location: </span>{locationStr}</li>}
                    </ul>
                  </>
                ) : (
                  <em style={{ color: '#aaa', fontSize: 12 }}>No workplace assigned.</em>
                )}
              </div>
            )}

            {/* Role & Responsibilities */}
            <SectionLabel>Roles &amp; Responsibilities</SectionLabel>

            {/* Position Description */}
            {(isAdmin && isEditMode) ? (
              <Editable onClick={onEditDescription}>
                {position.description ? (
                  <div style={{ padding: '4px 6px', fontSize: 13, color: '#444', lineHeight: 1.6 }}>
                    {position.description}
                  </div>
                ) : (
                  <div style={{ padding: '4px 6px', fontSize: 12, color: '#aaa', fontStyle: 'italic' }}>
                    Click to add a position description…
                  </div>
                )}
              </Editable>
            ) : (position.description || isSyncMode) ? (
              <div>
                {isSyncMode && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2 }}>
                    <SyncBtn section="description" />
                  </div>
                )}
                {position.description ? (
                  <div style={{ padding: '4px 6px', fontSize: 13, color: '#444', lineHeight: 1.6 }}>
                    {position.description}
                  </div>
                ) : (
                  <div style={{ padding: '4px 6px', fontSize: 12, color: '#aaa', fontStyle: 'italic' }}>No description</div>
                )}
              </div>
            ) : null}

            {/* Roles & Main Tasks */}
            {(roles.length > 0 || (isAdmin && isEditMode)) && (
              <div style={{ marginTop: 4 }}>
                {roles.map((role, ri) => {
                  const roleContent = (
                    <div style={{ padding: '0 6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                        <div className="doc-h2" style={{ color: '#444' }}>
                          {role.title || `Role ${ri + 1}`}
                        </div>
                        <SyncBtn section="role" roleId={role.id} />
                      </div>
                      {Array.isArray(role.tasks) && role.tasks.length > 0 && (
                        <ol style={{ margin: 0, paddingLeft: 20 }}>
                          {role.tasks.map((task, ti) => (
                            <li key={task.id || ti} style={{ fontSize: 12, color: '#555', marginBottom: 2, lineHeight: 1.5, listStyle: 'decimal' }}>
                              {task.text}
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  );
                  return (isAdmin && isEditMode) ? (
                    <Editable key={role.id || ri} onClick={() => onEditRole(role.id, ri)}>
                      {roleContent}
                    </Editable>
                  ) : (
                    <div key={role.id || ri} style={{ marginBottom: 10 }}>
                      {roleContent}
                    </div>
                  );
                })}
                {(isAdmin && isEditMode) && (
                  <Editable onClick={onAddRole}>
                    <div style={{ padding: '4px 6px' }}>
                      <em style={{ color: '#aaa', fontSize: 12 }}>Click here to add another role…</em>
                    </div>
                  </Editable>
                )}
              </div>
            )}

            {/* Benefits */}
            {(isAdmin && isEditMode) ? (
              <>
                <SectionLabel>Benefits</SectionLabel>
                <Editable onClick={onEditBenefits}>
                  <div style={{ padding: '4px 6px' }}>
                    {benefits.length === 0 ? (
                      <em style={{ color: '#aaa', fontSize: 12 }}>Click to select benefits…</em>
                    ) : (
                      <ul style={{ margin: 0, paddingLeft: 16, listStyle: 'disc' }}>
                        {benefits.map((b) => (
                          <li key={b} style={{ fontSize: 12, color: '#444', marginBottom: 2 }}>{b}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Editable>
              </>
            ) : (benefits.length > 0 || isSyncMode) ? (
              <>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 16 }}>
                  <DocH1 color={C.teal} style={{ flex: 1, marginBottom: 6 }}>Benefits</DocH1>
                  <SyncBtn section="benefits" />
                </div>
                {benefits.length > 0 ? (
                  <div style={{ padding: '0 6px' }}>
                    <ul style={{ margin: 0, paddingLeft: 16, listStyle: 'disc' }}>
                      {benefits.map((b) => (
                        <li key={b} style={{ fontSize: 12, color: '#444', marginBottom: 2 }}>{b}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div style={{ padding: '4px 6px' }}>
                    <em style={{ color: '#aaa', fontSize: 12 }}>No benefits defined</em>
                  </div>
                )}
              </>
            ) : null}

          </PosSubSection>

          {/* ── Sub-section: Expected Profile of Candidates ── */}
          <PosSubSection title="Expected Profile of Candidates" posId={position.id} subKey="ideal">

            {/* Languages */}
            {(icLangs.length > 0 || (isAdmin && isEditMode) || isSyncMode) && (() => {
              const content = (
                <div style={{ padding: '0 6px', marginTop: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                    <div className="doc-h2" style={{ color: '#444' }}>Languages</div>
                    <SyncBtn section="ic.languages" />
                  </div>
                  {icLangs.length > 0 ? (
                    <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                      {icLangs.map((l, i) => {
                        const name = typeof l === 'string' ? l : l.name;
                        const req  = typeof l === 'string' ? null : l.requirement;
                        return (
                          <li key={l.id || i} style={{ fontSize: 12, color: '#555', marginBottom: 2, lineHeight: 1.5 }}>
                            {name}<ReqLabel req={req} />
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <em style={{ color: '#aaa', fontSize: 12 }}>
                      {isEditMode ? 'Click to add languages…' : 'None defined'}
                    </em>
                  )}
                </div>
              );
              return (isAdmin && isEditMode)
                ? <Editable key="langs" onClick={() => onEditIdeal({ section: 'languages' })}>{content}</Editable>
                : <div key="langs" style={{ marginBottom: 10 }}>{content}</div>;
            })()}

            {/* Education */}
            {(icEdu.length > 0 || (isAdmin && isEditMode) || isSyncMode) && (() => {
              const content = (
                <div style={{ padding: '0 6px', marginTop: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                    <div className="doc-h2" style={{ color: '#444' }}>Education</div>
                    <SyncBtn section="ic.education" />
                  </div>
                  {icEdu.length > 0 ? (
                    <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                      {icEdu.map(e => (
                        <li key={e.id} style={{ fontSize: 12, color: '#555', marginBottom: 2, lineHeight: 1.5 }}>
                          {e.minLevel ? `${e.minLevel} in ` : ''}{e.fieldOfStudy}
                          <ReqLabel req={e.requirement} />
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <em style={{ color: '#aaa', fontSize: 12 }}>
                      {isEditMode ? 'Click to add education requirements…' : 'None defined'}
                    </em>
                  )}
                </div>
              );
              return (isAdmin && isEditMode)
                ? <Editable key="edu" onClick={() => onEditIdeal({ section: 'education' })}>{content}</Editable>
                : <div key="edu" style={{ marginBottom: 10 }}>{content}</div>;
            })()}

            {/* Experience */}
            {(icExp.length > 0 || (isAdmin && isEditMode) || isSyncMode) && (() => {
              const content = (
                <div style={{ padding: '0 6px', marginTop: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                    <div className="doc-h2" style={{ color: '#444' }}>Experience</div>
                    <SyncBtn section="ic.experience" />
                  </div>
                  {icExp.length > 0 ? (
                    <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                      {icExp.map(e => (
                        <li key={e.id} style={{ fontSize: 12, color: '#555', marginBottom: 2, lineHeight: 1.5 }}>
                          {e.minYears ? (EXP_YEAR_LABEL[e.minYears] || `${e.minYears}+ years`) : ''}
                          {e.positionName ? ` as ${e.positionName}` : ''}
                          <ReqLabel req={e.requirement} />
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <em style={{ color: '#aaa', fontSize: 12 }}>
                      {isEditMode ? 'Click to add experience requirements…' : 'None defined'}
                    </em>
                  )}
                </div>
              );
              return (isAdmin && isEditMode)
                ? <Editable key="exp" onClick={() => onEditIdeal({ section: 'experience' })}>{content}</Editable>
                : <div key="exp" style={{ marginBottom: 10 }}>{content}</div>;
            })()}

            {/* Skills & Knowledge — standard + custom categories */}
            {(() => {
              const customCats = Array.isArray(ic.customSkillCategories) ? ic.customSkillCategories : [];
              const allCats = [
                ...IDEAL_SKILL_SUBCATEGORIES,
                ...customCats.map(c => ({ key: c.key, label: c.label, isCustom: true })),
              ];
              const hasAnySkills = allCats.some(cat => (icSkillsObj[cat.key] || []).length > 0);
              if (!hasAnySkills && !(isAdmin && isEditMode) && !isSyncMode) return null;
              return (
                <div style={{ marginTop: 6, padding: '0 6px' }}>
                  <div className="doc-h2" style={{ color: '#444', marginBottom: 6 }}>Skills &amp; Knowledge</div>
                  {allCats.map((cat) => {
                    const items = Array.isArray(icSkillsObj[cat.key]) ? icSkillsObj[cat.key] : [];
                    if (!isEditMode && !isSyncMode && items.length === 0) return null;
                    const content = (
                      <div style={{ paddingLeft: 8, marginBottom: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 3 }}>
                          <div className="doc-h3" style={{ color: C.teal }}>{cat.label}</div>
                          <SyncBtn section="ic.skills" catKey={cat.key} catLabel={cat.label} />
                        </div>
                        {items.length > 0 ? (
                          <ul style={{ margin: 0, paddingLeft: 20, listStyle: 'disc' }}>
                            {items.map(s => (
                              <li key={s.id} style={{ fontSize: 12, color: '#555', marginBottom: 2, lineHeight: 1.5 }}>
                                {s.name}<ReqLabel req={s.requirement} />
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <em style={{ color: '#aaa', fontSize: 12 }}>
                            {isEditMode ? 'Click to add skills…' : 'None defined'}
                          </em>
                        )}
                      </div>
                    );
                    return (isAdmin && isEditMode)
                      ? <Editable key={cat.key} onClick={() => onEditIdeal({ section: 'skills', catKey: cat.key, catLabel: cat.label })}>{content}</Editable>
                      : <div key={cat.key} style={{ marginBottom: 8 }}>{content}</div>;
                  })}
                  {(isAdmin && isEditMode) && (
                    <Editable onClick={() => onEditIdeal({ section: 'skills-add' })}>
                      <div style={{ padding: '4px 6px' }}>
                        <em style={{ color: '#aaa', fontSize: 12 }}>Click here to add another skills section…</em>
                      </div>
                    </Editable>
                  )}
                </div>
              );
            })()}

            {!hasIdealData && !(isAdmin && isEditMode) && (
              <div style={{ padding: '8px 6px' }}>
                <em style={{ color: '#aaa', fontSize: 12 }}>No candidate profile defined for this position.</em>
              </div>
            )}

          </PosSubSection>

        </div>
      )}
    </div>
  );
};

// ── Edit Ad modal (metadata only: title, closing date, contact) ──
const EditAdModal = ({ ad, contacts, onSave, onCancel }) => {
  const [form, setForm] = useState({
    title:       ad.title,
    closingDate: ad.closingDate ? ad.closingDate.slice(0, 10) : '',
    contactId:   ad.contact?.id || '',
  });
  const [saving, setSaving] = useState(false);
  const setF = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await onSave({
        title:       form.title.trim(),
        closingDate: form.closingDate || null,
        contactId:   form.contactId  || null,
      });
    } finally { setSaving(false); }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 1000 }}>
      <div className="modal-box" style={{ maxWidth: 520, width: '92vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Edit Job Ad</h3>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} style={{ padding: '4px 6px' }}>
            <X size={15} />
          </button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto', flex: 1, padding: '20px 24px' }}>
          <div style={fieldWrap}>
            <label style={labelStyle}>Title *</label>
            <input type="text" value={form.title} maxLength={128} onChange={setF('title')} style={inputStyle} autoFocus />
            <div style={{ fontSize: 11, textAlign: 'right', marginTop: 3, color: form.title.length >= 120 ? '#CC0000' : '#999' }}>
              {form.title.length} / 128
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <div style={fieldWrap}>
              <label style={labelStyle}>Closing Date</label>
              <input type="date" value={form.closingDate} onChange={setF('closingDate')} style={inputStyle} />
            </div>
            <div style={fieldWrap}>
              <label style={labelStyle}>Contact Person</label>
              <select value={form.contactId} onChange={setF('contactId')} style={inputStyle}>
                <option value="">— None —</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.position ? ` — ${c.position}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Main component ────────────────────────────────────────

export const JobAdMockup = () => {
  const { adId }  = useParams();
  const navigate  = useNavigate();
  const { activeOrg, activeProfile, isAdmin } = useAuth();
  const { confirm, dialog } = useConfirm();

  const [lastAdId, setLastAdId]           = usePersistedState('tikhet:jobads:lastAdId', null);
  const [ad, setAd]                       = useState(null);
  const [contacts, setContacts]           = useState([]);
  const [snippets, setSnippets]           = useState([]);
  const [profileSocial, setProfileSocial] = useState(null);
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [editModal, setEditModal]         = useState(false);
  const [titleModal, setTitleModal]       = useState(false);
  const [richModal, setRichModal]         = useState(null);   // { field, currentHtml }
  const [plainModal, setPlainModal]       = useState(null);   // { posId, currentText }
  const [qrModal, setQrModal]             = useState(false);
  const [closingEdit, setClosingEdit]     = useState(false);
  const [expandedPositions, setExpanded]  = usePersistedState(`tikhet:mockup:${adId}:expanded`, {});
  const closingInputRef                   = useRef(null);
  const [jobDefs, setJobDefs]             = useState([]);
  const [allAds, setAllAds]               = useState([]);
  const [profileBenefits, setProfileBenefits] = useState(null);
  const [workplaces, setWorkplaces]       = useState([]);
  const [addPosModal, setAddPosModal]       = useState(false);
  const [editPosModal, setEditPosModal]     = useState(null); // { posId }
  const [wpModal, setWpModal]               = useState(null); // { posId, currentWorkplaceId }
  const [benefitsModal, setBenefitsModal]   = useState(null); // { posId }
  const [roleModal, setRoleModal]           = useState(null); // { posId, roleId, role, roleIndex }
  const [icpModal, setIcpModal]             = useState(null); // { posId, section, catKey?, catLabel? }
  const [contactsModal, setContactsModal]   = useState(false);
  const [positionModes, setPositionModes]     = usePersistedState(`tikhet:mockup:${adId}:modes`, {}); // posId → 'PREVIEW'|'EDIT'|'SYNC'
  const [undoStacks,    setUndoStacks]        = useState({}); // posId → snapshot[] (max 10, ephemeral)
  const [redoStacks,    setRedoStacks]        = useState({}); // posId → snapshot[] (ephemeral)
  const [undoRedoLoading, setUndoRedoLoading] = useState({}); // posId → bool
  const [syncModal,     setSyncModal]         = useState(null); // { posId, section }

  const load = useCallback(async () => {
    if (!activeOrg || !activeProfile) return;
    try {
      const [adRes, ctRes, jdRes, adsRes, profRes, wpRes, snipRes] = await Promise.all([
        jobAdAPI.getJobAd(activeOrg.id, activeProfile.id, adId),
        orgAPI.getProfileContacts(activeOrg.id, activeProfile.id),
        orgAPI.getJobDefs(activeOrg.id, activeProfile.id),
        jobAdAPI.getJobAds(activeOrg.id, activeProfile.id),
        orgAPI.getProfile(activeOrg.id, activeProfile.id),
        orgAPI.getWorkplaces(activeOrg.id, activeProfile.id),
        snippetAPI.getSnippets(activeOrg.id, activeProfile.id),
      ]);
      const adData = adRes.data.data.ad;
      setAd(adData);
      setContacts(ctRes.data.data.contacts || []);
      setSnippets(snipRes.data.data.snippets || []);
      setJobDefs(jdRes.data.data.jobDefs || []);
      setAllAds(adsRes.data.data.ads || []);
      const prof = profRes.data.data.profile;
      setProfileBenefits(prof?.benefits && typeof prof.benefits === 'object' ? prof.benefits : null);
      setProfileSocial({
        website:      prof?.website      || null,
        linkedinUrl:  prof?.linkedinUrl  || null,
        twitterUrl:   prof?.twitterUrl   || null,
        facebookUrl:  prof?.facebookUrl  || null,
        instagramUrl: prof?.instagramUrl || null,
      });
      setWorkplaces(wpRes.data.data.workplaces || []);
      // Only set the default (first position open) if nothing is persisted yet
      if (adData.positions?.length > 0) {
        setExpanded((prev) => Object.keys(prev).length === 0
          ? { [adData.positions[0].id]: true }
          : prev
        );
      }
    } finally { setLoading(false); }
  }, [activeOrg, activeProfile, adId]);

  useEffect(() => { load(); }, [load]);

  // Remember this ad across sessions — cleared by "Job Ads" link in toolbar
  useEffect(() => { if (adId) setLastAdId(adId); }, [adId]); // eslint-disable-line

  // Focus the date input once it's rendered
  useEffect(() => {
    if (closingEdit && closingInputRef.current) {
      closingInputRef.current.focus();
      closingInputRef.current.showPicker?.();
    }
  }, [closingEdit]);

  // ── Action handlers ──────────────────────────────────────

  const handlePublish = async () => {
    setSaving(true);
    try {
      const { data } = await jobAdAPI.publishJobAd(activeOrg.id, activeProfile.id, adId);
      setAd(data.data.ad);
    } finally { setSaving(false); }
  };

  const handleClose = async () => {
    const ok = await confirm('Close Job Ad', 'Close this ad? It will no longer appear to candidates.');
    if (!ok) return;
    setSaving(true);
    try {
      const { data } = await jobAdAPI.closeJobAd(activeOrg.id, activeProfile.id, adId);
      setAd(data.data.ad);
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    const ok = await confirm('Delete Job Ad', `Delete "${ad?.title}"? This cannot be undone.`);
    if (!ok) return;
    await jobAdAPI.deleteJobAd(activeOrg.id, activeProfile.id, adId);
    navigate('/employer/job-ads');
  };

  const handleSaveEdit = async (formData) => {
    const { data } = await jobAdAPI.updateJobAd(activeOrg.id, activeProfile.id, adId, formData);
    setAd(data.data.ad);
    setEditModal(false);
  };

  const handleSaveContacts = async (selectedIds) => {
    const { data } = await jobAdAPI.updateJobAd(activeOrg.id, activeProfile.id, adId, { contactIds: selectedIds });
    setAd(data.data.ad);
    setContactsModal(false);
  };

  const handleSaveTitle = async (title) => {
    const { data } = await jobAdAPI.updateJobAd(activeOrg.id, activeProfile.id, adId, { title });
    setAd(data.data.ad);
    setTitleModal(false);
  };

  const handleSaveRich = async (html) => {
    const { data } = await jobAdAPI.updateJobAd(
      activeOrg.id, activeProfile.id, adId,
      { [richModal.field]: html || null }
    );
    setAd(data.data.ad);
    setRichModal(null);
  };

  const handleSaveClosingDate = async (val) => {
    setClosingEdit(false);
    if (val === (ad.closingDate ? ad.closingDate.slice(0, 10) : '')) return; // no change
    const { data } = await jobAdAPI.updateJobAd(
      activeOrg.id, activeProfile.id, adId,
      { closingDate: val || null }
    );
    setAd(data.data.ad);
  };

  const handleSavePositionDesc = async (text) => {
    const { posId } = plainModal;
    pushUndo(posId);
    const { data } = await jobAdAPI.updatePosition(
      activeOrg.id, activeProfile.id, adId, posId,
      { description: text || null }
    );
    setAd((prev) => ({
      ...prev,
      positions: prev.positions.map((p) =>
        p.id === posId ? { ...p, description: data.data.position.description } : p
      ),
    }));
    setPlainModal(null);
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      toast.success('Link copied to clipboard');
    });
  };

  const handleAddPosition = async (posData) => {
    const { data } = await jobAdAPI.createPosition(activeOrg.id, activeProfile.id, adId, posData);
    const newPos = data.data.position;
    setAd((prev) => ({ ...prev, positions: [...prev.positions, newPos] }));
    setExpanded((prev) => ({ ...prev, [newPos.id]: true }));
    setAddPosModal(false);
  };

  const handleRemovePosition = async (posId) => {
    const ok = await confirm('Remove Position', 'Remove this position from the ad? This cannot be undone.');
    if (!ok) return;
    await jobAdAPI.deletePosition(activeOrg.id, activeProfile.id, adId, posId);
    setAd((prev) => ({ ...prev, positions: prev.positions.filter((p) => p.id !== posId) }));
  };

  const handleSavePositionCircumstances = async (formData) => {
    const { posId } = editPosModal;
    const { adminLevel, ...adPositionData } = formData;
    pushUndo(posId);
    const { data } = await jobAdAPI.updatePosition(
      activeOrg.id, activeProfile.id, adId, posId,
      { ...adPositionData, seniorityLevel: adminLevel || 'UNSPECIFIED' }
    );
    setAd((prev) => ({
      ...prev,
      positions: prev.positions.map((p) => p.id === posId ? data.data.position : p),
    }));
    setEditPosModal(null);
  };

  const handleSavePositionBenefits = async (benefits) => {
    const { posId } = benefitsModal;
    pushUndo(posId);
    const { data } = await jobAdAPI.updatePosition(activeOrg.id, activeProfile.id, adId, posId, { benefits });
    const updated = data.data.position;
    setAd((prev) => ({
      ...prev,
      positions: prev.positions.map((p) => p.id === posId ? updated : p),
    }));
    setBenefitsModal(null);
  };

  const handleSaveRole = async (roleId, { title, tasks }) => {
    const { posId, isNew } = roleModal;
    const pos = ad.positions.find((p) => p.id === posId);
    if (!pos) return;
    const currentRoles = Array.isArray(pos.roles) ? pos.roles : [];
    const updatedRoles = isNew
      ? [...currentRoles, { id: roleId, title, tasks }]
      : currentRoles.map((r) => r.id === roleId ? { ...r, title, tasks } : r);
    pushUndo(posId);
    const { data } = await jobAdAPI.updatePosition(activeOrg.id, activeProfile.id, adId, posId, { roles: updatedRoles });
    setAd((prev) => ({
      ...prev,
      positions: prev.positions.map((p) => p.id === posId ? { ...p, ...data.data.position } : p),
    }));
    setRoleModal(null);
  };

  const handleSaveIdeal = async (section, newValue) => {
    const { posId } = icpModal;
    const pos = ad.positions.find((p) => p.id === posId);
    if (!pos) return;
    const currentIdeal = (pos.idealCandidate && typeof pos.idealCandidate === 'object') ? pos.idealCandidate : {};
    const updated = { ...currentIdeal, [section]: newValue };
    pushUndo(posId);
    const { data } = await jobAdAPI.updatePosition(activeOrg.id, activeProfile.id, adId, posId, { idealCandidate: updated });
    setAd((prev) => ({
      ...prev,
      positions: prev.positions.map((p) => p.id === posId ? { ...p, ...data.data.position } : p),
    }));
    setIcpModal(null);
  };

  const handleSaveIdealSkills = async (catKey, items) => {
    const { posId } = icpModal;
    const pos = ad.positions.find((p) => p.id === posId);
    if (!pos) return;
    const currentIdeal = (pos.idealCandidate && typeof pos.idealCandidate === 'object') ? pos.idealCandidate : {};
    const currentSkills = (currentIdeal.skills && typeof currentIdeal.skills === 'object') ? currentIdeal.skills : {};
    const updated = { ...currentIdeal, skills: { ...currentSkills, [catKey]: items } };
    pushUndo(posId);
    const { data } = await jobAdAPI.updatePosition(activeOrg.id, activeProfile.id, adId, posId, { idealCandidate: updated });
    setAd((prev) => ({
      ...prev,
      positions: prev.positions.map((p) => p.id === posId ? { ...p, ...data.data.position } : p),
    }));
    setIcpModal(null);
  };

  const handleSaveNewSkillCategory = async ({ key, label, items }) => {
    const { posId } = icpModal;
    const pos = ad.positions.find((p) => p.id === posId);
    if (!pos) return;
    const currentIdeal = (pos.idealCandidate && typeof pos.idealCandidate === 'object') ? pos.idealCandidate : {};
    const currentSkills = (currentIdeal.skills && typeof currentIdeal.skills === 'object') ? currentIdeal.skills : {};
    const currentCustom = Array.isArray(currentIdeal.customSkillCategories) ? currentIdeal.customSkillCategories : [];
    const updated = {
      ...currentIdeal,
      skills: { ...currentSkills, [key]: items },
      customSkillCategories: [...currentCustom, { key, label }],
    };
    pushUndo(posId);
    const { data } = await jobAdAPI.updatePosition(activeOrg.id, activeProfile.id, adId, posId, { idealCandidate: updated });
    setAd((prev) => ({
      ...prev,
      positions: prev.positions.map((p) => p.id === posId ? { ...p, ...data.data.position } : p),
    }));
    setIcpModal(null);
  };

  const handleSaveWorkplace = async (workplaceId, hideWorkplaceName) => {
    const { posId } = wpModal;
    pushUndo(posId);
    const { data } = await jobAdAPI.updatePosition(activeOrg.id, activeProfile.id, adId, posId, { workplaceId, hideWorkplaceName });
    const updated = data.data.position;
    setAd((prev) => ({
      ...prev,
      positions: prev.positions.map((p) => p.id === posId ? updated : p),
    }));
    setWpModal(null);
  };

  const positionRefs = useRef({});

  const togglePosition = (posId) =>
    setExpanded((prev) => ({ ...prev, [posId]: !prev[posId] }));

  const focusPosition = (posId) => {
    const collapsed = Object.fromEntries(ad.positions.map((p) => [p.id, false]));
    setExpanded({ ...collapsed, [posId]: true });
    setTimeout(() => {
      positionRefs.current[posId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const setPositionMode = (posId, mode) => {
    setPositionModes((prev) => ({ ...prev, [posId]: mode }));
    if (mode === 'EDIT' || mode === 'SYNC') {
      setExpanded((prev) => ({ ...prev, [posId]: true }));
    }
  };

  // ── Snapshot helpers ───────────────────────────────────────
  const snapshotPos = (pos) => ({
    description: pos.description, workplaceId: pos.workplaceId,
    hideWorkplaceName: pos.hideWorkplaceName, schedule: pos.schedule,
    workArrangement: pos.workArrangement, salaryMin: pos.salaryMin,
    salaryMax: pos.salaryMax, salaryCurrency: pos.salaryCurrency,
    salaryPeriod: pos.salaryPeriod, salaryVisible: pos.salaryVisible,
    vacancies: pos.vacancies, openToForeignNationals: pos.openToForeignNationals,
    benefits: pos.benefits, roles: pos.roles,
    idealCandidate: pos.idealCandidate, seniorityLevel: pos.seniorityLevel,
  });

  const pushUndo = (posId) => {
    const pos = ad.positions.find((p) => p.id === posId);
    if (!pos) return;
    const snap = JSON.parse(JSON.stringify(snapshotPos(pos)));
    setUndoStacks((prev) => ({ ...prev, [posId]: [...(prev[posId] || []), snap].slice(-10) }));
    setRedoStacks((prev) => ({ ...prev, [posId]: [] }));
  };

  const applySnapshot = async (posId, snapshot) => {
    const { data } = await jobAdAPI.updatePosition(activeOrg.id, activeProfile.id, adId, posId, snapshot);
    setAd((prev) => ({
      ...prev,
      positions: prev.positions.map((p) => p.id === posId ? { ...p, ...data.data.position } : p),
    }));
  };

  const handleUndo = async (posId) => {
    const stack = undoStacks[posId] || [];
    if (!stack.length) return;
    const snapshot = stack[stack.length - 1];
    const current  = ad.positions.find((p) => p.id === posId);
    setUndoRedoLoading((prev) => ({ ...prev, [posId]: true }));
    try {
      setRedoStacks((prev) => ({ ...prev, [posId]: [...(prev[posId] || []), JSON.parse(JSON.stringify(snapshotPos(current)))].slice(-10) }));
      setUndoStacks((prev) => ({ ...prev, [posId]: stack.slice(0, -1) }));
      await applySnapshot(posId, snapshot);
    } finally {
      setUndoRedoLoading((prev) => ({ ...prev, [posId]: false }));
    }
  };

  const handleRedo = async (posId) => {
    const stack = redoStacks[posId] || [];
    if (!stack.length) return;
    const snapshot = stack[stack.length - 1];
    const current  = ad.positions.find((p) => p.id === posId);
    setUndoRedoLoading((prev) => ({ ...prev, [posId]: true }));
    try {
      setUndoStacks((prev) => ({ ...prev, [posId]: [...(prev[posId] || []), JSON.parse(JSON.stringify(snapshotPos(current)))].slice(-10) }));
      setRedoStacks((prev) => ({ ...prev, [posId]: stack.slice(0, -1) }));
      await applySnapshot(posId, snapshot);
    } finally {
      setUndoRedoLoading((prev) => ({ ...prev, [posId]: false }));
    }
  };

  const handleSync = async (posId, section, { roleId, catKey } = {}) => {
    const pos = ad.positions.find((p) => p.id === posId);
    if (!pos?.jobDefinitionId) return;
    const currentIC = pos.jobDefinition?.idealCandidate || {};
    const posIC = pos.idealCandidate || {};
    let patch = {};

    if (section === 'description') {
      patch = { description: pos.description ?? null };
    } else if (section === 'role') {
      const posRole = (pos.roles || []).find(r => r.id === roleId);
      if (!posRole) return;
      const jdRoles = Array.isArray(pos.jobDefinition?.roles) ? [...pos.jobDefinition.roles] : [];
      const idx = jdRoles.findIndex(r => r.id === roleId);
      if (idx >= 0) jdRoles[idx] = posRole; else jdRoles.push(posRole);
      patch = { roles: jdRoles };
    } else if (section === 'benefits') {
      patch = { benefits: pos.benefits ?? null };
    } else if (section === 'ic.languages') {
      patch = { idealCandidate: { ...currentIC, languages: posIC.languages ?? null } };
    } else if (section === 'ic.education') {
      patch = { idealCandidate: { ...currentIC, education: posIC.education ?? null } };
    } else if (section === 'ic.experience') {
      patch = { idealCandidate: { ...currentIC, experience: posIC.experience ?? null } };
    } else if (section === 'ic.skills' && catKey) {
      const posSkills = (posIC.skills && !Array.isArray(posIC.skills)) ? posIC.skills : {};
      const jdSkills  = (currentIC.skills && !Array.isArray(currentIC.skills)) ? currentIC.skills : {};
      patch = { idealCandidate: { ...currentIC, skills: { ...jdSkills, [catKey]: posSkills[catKey] ?? null } } };
    } else if (section === 'details') {
      patch = { seniorityLevel: pos.seniorityLevel || 'UNSPECIFIED', schedule: pos.schedule || null };
    } else if (section === 'workplace') {
      patch = { workplaceId: pos.workplaceId || null };
    }

    if (!Object.keys(patch).length) return;
    const { data } = await orgAPI.updateJobDef(activeOrg.id, activeProfile.id, pos.jobDefinitionId, patch);
    // Update the embedded jobDefinition in local state so comparison stays current
    setAd((prev) => ({
      ...prev,
      positions: prev.positions.map((p) =>
        p.id === posId ? { ...p, jobDefinition: { ...p.jobDefinition, ...data.data.jobDef } } : p
      ),
    }));
    setSyncModal(null);
    toast.success('Synced to profile template');
  };

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading…</div>;
  if (!ad)     return <div style={{ padding: 32, color: '#CC0000' }}>Job ad not found.</div>;

  const status = STATUS[ad.status] || STATUS.DRAFT;
  const closingDateStr = ad.closingDate
    ? new Date(ad.closingDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  const adIdDisplay = ad.adNumber != null
    ? String(ad.adNumber).padStart(4, '0')
    : ad.id.slice(-8).toUpperCase();

  const tbBtn = (extra = {}) => ({
    background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
    color: 'white', borderRadius: 4, padding: '3px 10px', cursor: 'pointer',
    fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4,
    fontFamily: 'inherit', ...extra,
  });

  const iconBtn = {
    background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)',
    cursor: 'pointer', padding: '2px 3px', display: 'flex', alignItems: 'center',
    borderRadius: 3, lineHeight: 1,
  };

  return (
    <div>
      {dialog}

      {/* ── Modals ── */}
      {editModal && (
        <EditAdModal ad={ad} contacts={contacts} onSave={handleSaveEdit} onCancel={() => setEditModal(false)} />
      )}
      {titleModal && (
        <TitleModal
          currentTitle={ad.title}
          positions={ad.positions}
          onSave={handleSaveTitle}
          onCancel={() => setTitleModal(false)}
        />
      )}
      {richModal && (
        <RichTextModal
          title={richModal.field === 'description' ? 'Ad Description' : 'How to Apply'}
          initialHtml={richModal.currentHtml}
          maxChars={2048}
          snippets={snippets}
          onSave={handleSaveRich}
          onCancel={() => setRichModal(null)}
        />
      )}
      {plainModal && (
        <PlainTextModal
          title="Position Description"
          initialText={plainModal.currentText}
          maxChars={1024}
          snippets={snippets}
          onSave={handleSavePositionDesc}
          onCancel={() => setPlainModal(null)}
        />
      )}
      {qrModal && <QrModal onClose={() => setQrModal(false)} />}
      {addPosModal && (
        <AddPositionModal
          jobDefs={jobDefs}
          allAds={allAds}
          currentAdId={adId}
          onAdd={handleAddPosition}
          onCancel={() => setAddPosModal(false)}
        />
      )}
      {editPosModal && (
        <EditPositionCircumstancesModal
          position={ad.positions.find((p) => p.id === editPosModal.posId)}
          onSave={handleSavePositionCircumstances}
          onCancel={() => setEditPosModal(null)}
        />
      )}
      {benefitsModal && (
        <EditPositionBenefitsModal
          position={ad.positions.find((p) => p.id === benefitsModal.posId)}
          profileBenefits={profileBenefits}
          onSave={handleSavePositionBenefits}
          onCancel={() => setBenefitsModal(null)}
        />
      )}
      {wpModal && (
        <SelectWorkplaceModal
          workplaces={workplaces}
          currentWorkplaceId={wpModal.currentWorkplaceId}
          currentHideWorkplaceName={wpModal.hideWorkplaceName}
          onSave={handleSaveWorkplace}
          onCancel={() => setWpModal(null)}
        />
      )}
      {roleModal && (
        <EditRoleModal
          key={roleModal.roleId}
          role={roleModal.role}
          roleIndex={roleModal.roleIndex}
          onSave={handleSaveRole}
          onCancel={() => setRoleModal(null)}
        />
      )}
      {icpModal?.section === 'languages' && (() => {
        const pos = ad.positions.find((p) => p.id === icpModal.posId);
        const ic  = pos?.jobDefinition?.idealCandidate || {};
        return (
          <IcpLanguagesModal
            initial={Array.isArray(ic.languages) ? ic.languages : []}
            onSave={(items) => handleSaveIdeal('languages', items)}
            onCancel={() => setIcpModal(null)}
          />
        );
      })()}
      {icpModal?.section === 'education' && (() => {
        const pos = ad.positions.find((p) => p.id === icpModal.posId);
        const ic  = pos?.jobDefinition?.idealCandidate || {};
        return (
          <IcpEducationModal
            initial={Array.isArray(ic.education) ? ic.education : []}
            onSave={(items) => handleSaveIdeal('education', items)}
            onCancel={() => setIcpModal(null)}
          />
        );
      })()}
      {icpModal?.section === 'experience' && (() => {
        const pos = ad.positions.find((p) => p.id === icpModal.posId);
        const ic  = pos?.jobDefinition?.idealCandidate || {};
        return (
          <IcpExperienceModal
            initial={Array.isArray(ic.experience) ? ic.experience : []}
            onSave={(items) => handleSaveIdeal('experience', items)}
            onCancel={() => setIcpModal(null)}
          />
        );
      })()}
      {icpModal?.section === 'skills' && (() => {
        const pos = ad.positions.find((p) => p.id === icpModal.posId);
        const ic  = pos?.jobDefinition?.idealCandidate || {};
        const skills = (ic.skills && typeof ic.skills === 'object') ? ic.skills : {};
        return (
          <IcpSkillsModal
            title={icpModal.catLabel}
            initial={Array.isArray(skills[icpModal.catKey]) ? skills[icpModal.catKey] : []}
            onSave={(items) => handleSaveIdealSkills(icpModal.catKey, items)}
            onCancel={() => setIcpModal(null)}
          />
        );
      })()}
      {icpModal?.section === 'skills-add' && (
        <IcpAddSkillCategoryModal
          onSave={handleSaveNewSkillCategory}
          onCancel={() => setIcpModal(null)}
        />
      )}
      {contactsModal && (() => {
        const selectedIds = Array.isArray(ad.contactIds) ? ad.contactIds : [];
        const wpContactIds = (ad.positions || []).flatMap(pos => {
          const wpId = pos.workplace?.id || pos.jobDefinition?.workplace?.id;
          if (!wpId) return [];
          return contacts.filter(c => c.workplaceId === wpId).map(c => c.id);
        });
        return (
          <SelectContactsModal
            contacts={contacts}
            selectedIds={selectedIds}
            workplaceContactIds={wpContactIds}
            onSave={handleSaveContacts}
            onCancel={() => setContactsModal(false)}
          />
        );
      })()}

      {syncModal && (() => {
        const pos = ad.positions.find(p => p.id === syncModal.posId);
        if (!pos) return null;
        return (
          <SyncModal
            pos={pos}
            section={syncModal.section}
            roleId={syncModal.roleId}
            catKey={syncModal.catKey}
            catLabel={syncModal.catLabel}
            onConfirm={() => handleSync(syncModal.posId, syncModal.section, { roleId: syncModal.roleId, catKey: syncModal.catKey })}
            onCancel={() => setSyncModal(null)}
          />
        );
      })()}

      {/* ── Toolbar ── */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontWeight: 700, fontSize: 15, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {ad.title}
        </span>

        <span style={{
          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, flexShrink: 0,
          background: status.bg, color: status.color, border: `1px solid ${status.color}40`,
        }}>{status.label}</span>

        {isAdmin && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            {(ad.status === 'DRAFT' || ad.status === 'PAUSED') && (
              <button
                style={tbBtn({ background: 'rgba(40,167,69,0.3)', border: '1px solid rgba(40,167,69,0.6)' })}
                onClick={handlePublish} disabled={saving}
              >
                <CheckCircle size={12} /> Publish
              </button>
            )}
            {ad.status === 'ACTIVE' && (
              <button style={tbBtn()} onClick={handleClose} disabled={saving}>Close Ad</button>
            )}
            <EditBtn onClick={() => setEditModal(true)} title="Edit" />
            <DelBtn onClick={handleDelete} title="Delete" />
          </div>
        )}

      </div>

      {/* ── Back link ── */}
      <div style={{ paddingBottom: 6 }}>
        <button
          onClick={() => { setLastAdId(null); navigate('/employer/job-ads'); }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 5,
            color: 'var(--text-muted)', fontSize: 12, fontWeight: 600,
            padding: '4px 0', fontFamily: 'inherit',
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#3D80CC'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
        >
          <ArrowLeft size={13} /> Job Ads
        </button>
      </div>

      {/* ── Document ── */}
      <div style={{ padding: '8px 0 16px' }}>
        <div style={{
          borderRadius: 6,
          border: `1px solid ${C.docBorder}`,
          overflow: 'hidden',
          fontFamily: "'DM Sans', sans-serif",
          background: C.white,
          boxShadow: '0 4px 24px rgba(14,78,149,0.12)',
        }}>

          {/* ── Ad Header ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '20px 22px', borderBottom: '1px solid #e0e0e0' }}>
            {/* Logo */}
            <div style={{
              width: 72, height: 72, flexShrink: 0, borderRadius: 4,
              background: '#ebebeb', display: 'flex', alignItems: 'center',
              justifyContent: 'center', overflow: 'hidden',
            }}>
              {activeProfile?.logoUrl
                ? <img src={activeProfile.logoUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                : <span style={{ fontSize: 11, color: '#bbb', textAlign: 'center' }}>LOGO</span>
              }
            </div>
            {/* Title */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {isAdmin ? (
                <Editable onClick={() => setTitleModal(true)}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: C.adTitle, lineHeight: 1.2, padding: '2px 0' }}>
                    {ad.title}
                  </div>
                </Editable>
              ) : (
                <div style={{ fontSize: 24, fontWeight: 700, color: C.adTitle, lineHeight: 1.2 }}>
                  {ad.title}
                </div>
              )}
              <div style={{ fontSize: 15, marginTop: 4 }}>
                <span style={{ fontWeight: 700, color: '#444' }}>with </span>
                <span style={{ fontWeight: 700, color: C.maroon }}>
                  {activeProfile?.profileName || '—'}
                </span>
              </div>
            </div>
          </div>

          {/* ── Two-column body ── */}
          <div style={{ display: 'flex' }}>

            {/* ── LEFT SIDEBAR ── */}
            <div style={{ width: 200, flexShrink: 0, background: C.sidebarBg, borderRight: '1px solid #ddd' }}>

              {/* Ad ID + share icons */}
              <div style={{
                background: C.blue, color: 'white',
                padding: '7px 10px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, opacity: 0.8 }}>ID</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#cde3f7', letterSpacing: '0.08em' }}>
                    {adIdDisplay}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button onClick={handleShare} title="Copy link" style={iconBtn}>
                    <Share2 size={14} />
                  </button>
                  <button onClick={() => window.print()} title="Print" style={iconBtn}>
                    <Printer size={14} />
                  </button>
                  <button onClick={() => setQrModal(true)} title="QR Code" style={iconBtn}>
                    <QrCode size={14} />
                  </button>
                </div>
              </div>

              {/* Closing date */}
              {isAdmin ? (
                <Editable
                  onClick={() => !closingEdit && setClosingEdit(true)}
                  style={{ display: 'block' }}
                >
                  <div style={{
                    background: C.closingBg, padding: '7px 12px',
                    display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                  }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: C.teal, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      Closing Date:
                    </span>
                    {closingEdit ? (
                      <input
                        ref={closingInputRef}
                        type="date"
                        defaultValue={ad.closingDate ? ad.closingDate.slice(0, 10) : ''}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleSaveClosingDate(e.target.value)}
                        onBlur={(e) => handleSaveClosingDate(e.target.value)}
                        style={{
                          border: 'none', background: 'transparent', fontSize: 12,
                          color: '#333', fontFamily: 'inherit', outline: 'none',
                          padding: 0, width: '100%',
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: 12, color: '#333' }}>
                        {closingDateStr || <em style={{ color: '#999' }}>Not set</em>}
                      </span>
                    )}
                  </div>
                </Editable>
              ) : (
                <div style={{ background: C.closingBg, padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: C.teal, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    Closing Date:
                  </span>
                  <span style={{ fontSize: 12, color: '#333' }}>
                    {closingDateStr || <em style={{ color: '#999' }}>Not set</em>}
                  </span>
                </div>
              )}

              {/* Positions included */}
              <div style={{ marginTop: 8 }}>
                <SidebarBar>Positions Included</SidebarBar>
                {ad.positions.length === 0 && !isAdmin && (
                  <SidebarPlaceholder minHeight={40}>No positions yet.</SidebarPlaceholder>
                )}
                {ad.positions.length > 0 && (
                  <div style={{ padding: '4px 0 2px' }}>
                    {ad.positions.map((p) => {
                      const active = !!expandedPositions[p.id];
                      return (
                        <div key={p.id}
                          onClick={() => focusPosition(p.id)}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            fontSize: 11, padding: '4px 6px 4px 10px', lineHeight: 1.4,
                            cursor: 'pointer',
                            color: active ? C.blue : '#444',
                            fontWeight: active ? 700 : 400,
                            borderLeft: `3px solid ${active ? C.blue : 'transparent'}`,
                            background: active ? '#e8f0fa' : 'transparent',
                          }}
                          onMouseEnter={e => { if (!active) { e.currentTarget.style.background = '#eee'; } }}
                          onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; } }}
                        >
                          <span>{p.jobDefinition?.name || 'Position'}</span>
                          {isAdmin && (
                            <span onClick={e => e.stopPropagation()}>
                              <DelBtn onClick={() => handleRemovePosition(p.id)} title="Remove Position"
                                size={10} style={{ height: 16, minWidth: 16, padding: '0 2px' }} />
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {isAdmin && (
                  <Editable onClick={() => setAddPosModal(true)}>
                    <div style={{ padding: '5px 10px' }}>
                      <em style={{ color: '#aaa', fontSize: 11 }}>Click here to add a position…</em>
                    </div>
                  </Editable>
                )}
              </div>

              {/* Contact details */}
              {(() => {
                const selectedIds  = Array.isArray(ad.contactIds) ? ad.contactIds : [];
                const selectedContacts = selectedIds
                  .map(id => contacts.find(c => c.id === id))
                  .filter(Boolean);

                // Contacts linked to workplaces of any position in this ad
                const wpContactIds = new Set(
                  (ad.positions || []).flatMap(pos => {
                    const wpId = pos.workplace?.id || pos.jobDefinition?.workplace?.id;
                    if (!wpId) return [];
                    return contacts.filter(c => c.workplaceId === wpId).map(c => c.id);
                  })
                );

                const contactCard = (c) => (
                  <div key={c.id} style={{ padding: '7px 10px', borderBottom: '1px solid #eee' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#333' }}>
                      {c.name}
                    </div>
                    {c.emails?.[0] && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.blue, marginTop: 4 }}>
                        <Mail size={11} style={{ flexShrink: 0 }} />
                        {c.emails[0].email || c.emails[0].value}
                      </div>
                    )}
                    {c.phones?.[0] && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#555', marginTop: 2 }}>
                        <Phone size={11} style={{ flexShrink: 0 }} />
                        {c.phones[0].phone || [c.phones[0].code, c.phones[0].number].filter(Boolean).join(' ')}
                      </div>
                    )}
                  </div>
                );

                const inner = (
                  <div>
                    {selectedContacts.length > 0
                      ? selectedContacts.map(contactCard)
                      : (isAdmin
                          ? <div style={{ padding: '8px 10px', fontSize: 12, color: '#aaa', fontStyle: 'italic' }}>
                              Click to assign contacts…
                            </div>
                          : <SidebarPlaceholder minHeight={70}>No contacts assigned.</SidebarPlaceholder>
                        )
                    }
                  </div>
                );

                return (
                  <div style={{ marginTop: 4 }}>
                    <SidebarBar>Contact Details</SidebarBar>
                    {isAdmin ? (
                      <Editable onClick={() => setContactsModal(true)}>{inner}</Editable>
                    ) : inner}

                    {/* Social Links */}
                    {(() => {
                      const links = [
                        { label: 'Website',   url: profileSocial?.website      },
                        { label: 'LinkedIn',  url: profileSocial?.linkedinUrl  },
                        { label: 'Twitter',   url: profileSocial?.twitterUrl   },
                        { label: 'Facebook',  url: profileSocial?.facebookUrl  },
                        { label: 'Instagram', url: profileSocial?.instagramUrl },
                      ].filter(l => l.url);

                      if (!links.length && !isAdmin) return null;
                      return (
                        <div style={{ marginTop: 6 }}>
                          <SidebarBar>Social</SidebarBar>
                          <div style={{ padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                            {links.map(l => (
                              <a key={l.label} href={l.url} target="_blank" rel="noopener noreferrer"
                                style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11,
                                  color: C.blue, textDecoration: 'none', fontWeight: 500 }}
                                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                              >
                                <ExternalLink size={9} style={{ flexShrink: 0 }} />
                                {l.label}
                              </a>
                            ))}
                            {!links.length && isAdmin && (
                              <em style={{ fontSize: 11, color: '#bbb' }}>
                                Add social links via Profile settings
                              </em>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}
            </div>

            {/* ── RIGHT CONTENT ── */}
            <div style={{ flex: 1, minWidth: 0, paddingBottom: 20 }}>

              {/* Ad Description */}
              <SectionBar>Ad Description</SectionBar>
              <div style={{ padding: '10px 14px' }}>
                {isAdmin ? (
                  <Editable
                    onClick={() => setRichModal({ field: 'description', currentHtml: ad.description || '' })}
                  >
                    {ad.description ? (
                      <div
                        className="rte-editor"
                        style={{ fontSize: 14, lineHeight: 1.65, color: '#333', padding: '6px 4px' }}
                        dangerouslySetInnerHTML={{ __html: ad.description }}
                      />
                    ) : (
                      <div style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic', padding: '8px 4px' }}>
                        Click to add an ad description…
                      </div>
                    )}
                  </Editable>
                ) : ad.description ? (
                  <div className="rte-editor" style={{ fontSize: 14, lineHeight: 1.65, color: '#333' }}
                    dangerouslySetInnerHTML={{ __html: ad.description }} />
                ) : (
                  <div style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic' }}>No description.</div>
                )}
              </div>

              {/* Ad Positions */}
              <div style={{ display: 'flex', alignItems: 'center', background: C.blue, color: 'white', fontSize: 14, fontWeight: 500, padding: '6px 14px', gap: 8 }}>
                <span>Ad Positions</span>
              </div>
              <div style={{ padding: '10px 14px' }}>
                {ad.positions.length === 0 && !isAdmin ? (
                  <p style={{ fontSize: 13, color: '#aaa', fontStyle: 'italic', margin: 0 }}>
                    No positions added to this ad yet.
                  </p>
                ) : (
                  <>
                    {ad.positions.map((pos) => (
                      <div key={pos.id} ref={(el) => { positionRefs.current[pos.id] = el; }}>
                        <PositionRow
                          position={pos}
                          expanded={!!expandedPositions[pos.id]}
                          onToggle={() => togglePosition(pos.id)}
                          isAdmin={isAdmin}
                          mode={positionModes[pos.id] || 'PREVIEW'}
                          onChangeMode={(m) => setPositionMode(pos.id, m)}
                          canUndo={(undoStacks[pos.id] || []).length > 0}
                          canRedo={(redoStacks[pos.id] || []).length > 0}
                          onUndo={() => handleUndo(pos.id)}
                          onRedo={() => handleRedo(pos.id)}
                          undoRedoLoading={!!undoRedoLoading[pos.id]}
                          adStatus={ad.status}
                          onEditDescription={() => setPlainModal({ posId: pos.id, currentText: pos.description || '' })}
                          onRemove={() => handleRemovePosition(pos.id)}
                          onEditCircumstances={() => setEditPosModal({ posId: pos.id })}
                          onEditBenefits={() => setBenefitsModal({ posId: pos.id })}
                          onEditWorkplace={() => setWpModal({ posId: pos.id, currentWorkplaceId: pos.workplace?.id || null, hideWorkplaceName: pos.hideWorkplaceName })}
                          onEditRole={(roleId, roleIndex) => {
                            const position = ad.positions.find((p) => p.id === pos.id);
                            const role = (Array.isArray(position?.roles) ? position.roles : []).find((r) => r.id === roleId);
                            if (role) setRoleModal({ posId: pos.id, roleId, role, roleIndex });
                          }}
                          onAddRole={() => {
                            const id = newId();
                            const position = ad.positions.find((p) => p.id === pos.id);
                            const roleIndex = (Array.isArray(position?.roles) ? position.roles : []).length;
                            setRoleModal({ posId: pos.id, roleId: id, role: { id, title: '', tasks: [] }, roleIndex, isNew: true });
                          }}
                          onEditIdeal={(opts) => setIcpModal({ posId: pos.id, ...opts })}
                          onSync={(section, extra) => setSyncModal({ posId: pos.id, section, ...extra })}
                        />
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* How to Apply */}
              <SectionBar>How to Apply</SectionBar>
              <div style={{ padding: '10px 14px' }}>
                {isAdmin ? (
                  <Editable
                    onClick={() => setRichModal({ field: 'howToApply', currentHtml: ad.howToApply || '' })}
                  >
                    {ad.howToApply ? (
                      <div
                        className="rte-editor"
                        style={{ fontSize: 14, lineHeight: 1.65, color: '#333', padding: '6px 4px' }}
                        dangerouslySetInnerHTML={{ __html: ad.howToApply }}
                      />
                    ) : (
                      <div style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic', padding: '8px 4px' }}>
                        Click to add how-to-apply instructions…
                      </div>
                    )}
                  </Editable>
                ) : ad.howToApply ? (
                  <div className="rte-editor" style={{ fontSize: 14, lineHeight: 1.65, color: '#333' }}
                    dangerouslySetInnerHTML={{ __html: ad.howToApply }} />
                ) : (
                  <div style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic' }}>No instructions provided.</div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
